import { formatValue } from "../../utils/chartFormatters";
// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Table,
    Tag,
    Card,
    Space,
    Statistic,
    Row,
    Col,
    Select,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    message,
    Divider,
    Switch,
    Tooltip,
    Timeline,
    DatePicker,
    Collapse,
    theme,
    Grid,
    List,
    Badge,
    Checkbox,
    Dropdown,
} from 'antd';
import { ShoppingCartOutlined, DollarOutlined, PlusOutlined, DeleteOutlined, EditOutlined, PhoneOutlined, HistoryOutlined, InfoCircleOutlined, SaveOutlined, FilterOutlined, DownloadOutlined, RiseOutlined, FallOutlined, QuestionCircleOutlined, PercentageOutlined, LineChartOutlined, BarChartOutlined, WarningOutlined, MessageOutlined, UpOutlined, DownOutlined, BankOutlined, CloseOutlined, LinkOutlined, CopyOutlined } from '@ant-design/icons';
import { ordersApi, productsApi, inventoryApi, crmApi, usersApi, channelsApi } from '../../api/client';
import { ContractorSelect } from './components/ContractorSelect';
import { ContractorMiniInfo } from './components/ContractorMiniInfo';
import OrderComments from './components/OrderComments';
import OrderProductsCell, { groupOrderLinesForDisplay } from './components/OrderProductsCell';
import { Contractor } from '../crm/types/contractor';
import dayjs from 'dayjs';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';

const { useBreakpoint } = Grid;

// Status labels in Polish
const statusLabels: Record<string, string> = {
    PENDING: 'Oczekujące',
    CONFIRMED: 'Potwierdzone',
    PROCESSING: 'W realizacji',
    PACKED: 'Spakowane',
    SHIPPED: 'Wysłane',
    DELIVERED: 'Dostarczone',
    COMPLETED: 'Zakończone',
    CANCELLED: 'Anulowane',
    REFUNDED: 'Zwrot',
    ON_HOLD: 'Wstrzymane',
};

// Courier tracking URL mapping - uses furgonetka.pl for universal tracking
function getTrackingUrl(trackingNumber: string, shippingMethod?: string): string | null {
    if (!trackingNumber) return null;

    const tn = trackingNumber.trim();
    const method = (shippingMethod || '').toLowerCase();

    // InPost - very reliable direct tracking
    if (method.includes('inpost') || method.includes('paczkomat')) {
        return `https://inpost.pl/sledzenie-przesylek?number=${tn}`;
    }

    // For all other couriers - use furgonetka.pl as universal tracker
    // It supports: DHL, DPD, UPS, GLS, FedEx, Poczta Polska, ORLEN, Allegro One, and more
    return `https://furgonetka.pl/zlokalizuj/${tn}`;
}

interface ShipmentStatusInfo {
    key: 'NONE' | 'READY' | 'IN_TRANSIT' | 'DELIVERED' | 'PROBLEM';
    label: string;
    color: string;
}

function getShipmentStatus(order: any): ShipmentStatusInfo {
    if (!order.trackingNumber) {
        return {
            key: 'NONE',
            label: 'Brak przesyłki',
            color: 'default',
        };
    }

    const problemStatuses = ['CANCELLED', 'REFUNDED', 'ON_HOLD'];
    if (problemStatuses.includes(order.status)) {
        return {
            key: 'PROBLEM',
            label: 'Problem z dostawą',
            color: 'error',
        };
    }

    // Check if channel is Allegro
    const isAllegro = order.channel?.name?.toLowerCase().includes('allegro') || !!order.externalId;

    if (order.deliveredAt || order.status === 'DELIVERED' || order.status === 'COMPLETED') {
        return {
            key: 'DELIVERED',
            label: isAllegro ? 'Dostarczona (zgodnie z systemem)' : 'Dostarczona',
            color: 'success',
        };
    }

    if (order.shippedAt || order.status === 'SHIPPED') {
        return {
            key: 'IN_TRANSIT',
            label: isAllegro ? 'W drodze (aktywne śledzenie)' : 'W drodze',
            color: 'processing',
        };
    }

    // trackingNumber exists, but not shipped or delivered yet
    return {
        key: 'READY',
        label: 'Przygotowana',
        color: 'warning',
    };
}


// Status History Card Component
function StatusHistoryCard({ orderId, plainLayout }: { orderId: string; plainLayout?: boolean }) {
    const { token } = theme.useToken();
    const { data: historyData, isLoading } = useQuery({
        queryKey: ['order-status-history', orderId],
        queryFn: () => ordersApi.getStatusHistory(orderId).then((res) => res.data),
        enabled: !!orderId,
    });

    const history = historyData || [];

    if (isLoading) {
        return plainLayout ? <div style={{ color: token.colorTextDescription }}>Ładowanie...</div> : <Card size="small" title="Historia statusów" loading />;
    }

    if (history.length === 0) {
        return plainLayout ? (
            <p style={{ color: token.colorTextDescription, fontSize: 12 }}>Brak zmian statusu</p>
        ) : (
            <Card size="small" title="Historia statusów">
                <p style={{ color: token.colorTextDescription }}>Brak zmian statusu</p>
            </Card>
        );
    }

    const timeline = (
        <Timeline
            items={history.map((h: any) => ({
                color: h.toStatus === 'CANCELLED' || h.toStatus === 'REFUNDED' ? 'red' :
                    h.toStatus === 'COMPLETED' || h.toStatus === 'DELIVERED' ? 'green' :
                        h.toStatus === 'SHIPPED' ? 'blue' : 'gray',
                children: (
                    <div>
                        <strong>{statusLabels[h.fromStatus] || h.fromStatus}</strong>
                        {' → '}
                        <strong>{statusLabels[h.toStatus] || h.toStatus}</strong>
                        <br />
                        <span style={{ color: token.colorTextDescription, fontSize: 11 }}>
                            {dayjs(h.createdAt).format('DD.MM.YYYY HH:mm')}
                            {h.changedBy && ` • ${h.changedBy.firstName || ''} ${h.changedBy.lastName || h.changedBy.email || ''}`}
                        </span>
                        {h.reason && (
                            <div style={{ color: token.colorTextDescription, fontSize: 11, marginTop: 2, fontStyle: 'italic' }}>
                                Powód: {h.reason}
                            </div>
                        )}
                    </div>
                ),
            }))}
        />
    );

    if (plainLayout) {
        return <div style={{ padding: '8px 0 0 0' }}>{timeline}</div>;
    }

    return (
        <Card size="small" title="Historia statusów">
            {timeline}
        </Card>
    );
}

const groupOrderLines = (lines: any[]) => {
    if (!lines) return [];
    const groupedMap = new Map<string, any>();
    for (const line of lines) {
        // Group by productId if available, otherwise fallback to sku, then name
        const key = line.productId || line.sku || line.name;
        if (!key) continue;

        if (groupedMap.has(key)) {
            const existing = groupedMap.get(key);
            existing.quantity = (existing.quantity || 0) + Number(line.quantity || 0);
            existing.total = (existing.total || 0) + Number(line.total || 0);
            existing.profit = (existing.profit || 0) + Number(line.profit || 0);
            existing.totalCost = (existing.totalCost || 0) + Number(line.totalCost || 0);
        } else {
            // Clone the line to avoid mutating the original query cache
            groupedMap.set(key, { 
                ...line, 
                quantity: Number(line.quantity || 0), 
                total: Number(line.total || 0), 
                profit: Number(line.profit || 0),
                totalCost: Number(line.totalCost || 0)
            });
        }
    }
    return Array.from(groupedMap.values());
};

const getShortOrderNumber = (record: any) => {
    if (!record) return '-';
    const orderNum = record.orderNumber || '';
    const date = dayjs(record.orderDate || record.createdAt);
    const yyyymm = date.format('YYYYMM');
    
    // Check if it's an Allegro order (orderNumber is a UUID or channel name is Allegro)
    const isAllegro = (record.channel?.name?.toLowerCase().includes('allegro') || 
                       /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(orderNum));
    
    if (isAllegro) {
        // Derive a consistent 5-digit number from the order ID/number
        let hash = 0;
        const str = record.id || orderNum;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        const num = Math.abs(hash % 90000) + 10000; // ensures 5 digits: 10000 to 99999
        return `ALG-${yyyymm}-${num}`;
    }
    
    if (orderNum.startsWith('ORD-')) {
        const match = orderNum.match(/^ORD-(\d{6})(\d{5})$/);
        if (match) {
            return `ORD-${match[1]}-${match[2]}`;
        }
        return orderNum;
    }
    
    // Fallback/other channels
    const channelPrefix = record.channel?.name ? record.channel.name.substring(0, 3).toUpperCase() : 'ERP';
    let hash = 0;
    const str = record.id || orderNum;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const num = Math.abs(hash % 90000) + 10000;
    return `${channelPrefix}-${yyyymm}-${num}`;
};

const getSalesType = (record: any) => {
    if (!record) return { label: 'Detal', color: 'green' };
    const explicitType = record.customFields?.salesType || record.metadata?.salesType;
    
    if (explicitType === 'komis' || explicitType === 'consignment') {
        return { label: 'Komis', color: 'purple' };
    }
    if (explicitType === 'b2b' || explicitType === 'B2B') {
        return { label: 'B2B', color: 'blue' };
    }
    if (explicitType === 'detal' || explicitType === 'retail' || explicitType === 'direct' || explicitType === 'direct_sale' || explicitType === 'allegro') {
        return { label: 'Detal', color: 'green' };
    }

    // Fallbacks
    if (record.channel?.name?.toLowerCase().includes('allegro')) {
        return { label: 'Detal', color: 'green' };
    }
    if (record.contractorId) {
        return { label: 'B2B', color: 'blue' };
    }
    return { label: 'Detal', color: 'green' };
};

const getBillingStatus = (record: any): { label: string; color: string; problem: boolean } => {
    if (!record) return { label: 'Nieopłacone', color: 'default', problem: false };
    const status = record.paymentStatus;

    if (status === 'PAID') {
        return { label: 'Opłacone', color: 'green', problem: false };
    }
    if (status === 'PARTIALLY_PAID') {
        return { label: 'Część. opłacone', color: 'orange', problem: true };
    }
    if (status === 'REFUNDED') {
        return { label: 'Zwrot', color: 'purple', problem: true };
    }
    if (status === 'FAILED') {
        return { label: 'Błąd płatności', color: 'red', problem: true };
    }

    // Check if it's overdue (pending payment older than 7 days)
    const orderDate = record.orderDate || record.createdAt;
    const isCancelled = record.status === 'CANCELLED' || record.status === 'REFUNDED';
    if (!isCancelled && orderDate && dayjs().diff(dayjs(orderDate), 'day') > 7) {
        return { label: 'Po terminie', color: 'red', problem: true };
    }

    // Skip payment warning for cancelled/refunded orders
    if (isCancelled) {
        return { label: '—', color: 'default', problem: false };
    }

    return { label: 'Oczekuje', color: 'gold', problem: false };
};

interface OrderProfitability {
    status: 'INCOMPLETE_COGS' | 'ESTIMATED_ALLEGRO' | 'COGS_ONLY' | 'FULL_DIRECT' | 'CANCELLED' | 'REFUNDED' | 'UNPAID' | 'PARTIALLY_PAID';
    label: string;
    description: string;
    color: string;
    severity: 'success' | 'warning' | 'info' | 'error';
    badgeLabel: string;
}

const getOrderProfitability = (record: any): OrderProfitability => {
    if (!record) {
        return {
            status: 'COGS_ONLY',
            label: 'Tylko COGS i wysyłka',
            badgeLabel: 'Tylko COGS',
            description: 'Brak danych o zamówieniu.',
            color: '#13c2c2',
            severity: 'info',
        };
    }

    if (record.status === 'CANCELLED') {
        return {
            status: 'CANCELLED',
            label: 'Nie dotyczy (Anulowane)',
            badgeLabel: 'Anulowane',
            description: 'Zamówienie zostało anulowane. Finanse są wyłączone z kalkulacji marży i obrotu.',
            color: '#8c8c8c', // neutral grey
            severity: 'info',
        };
    }

    if (record.status === 'REFUNDED' || record.paymentStatus === 'REFUNDED' || Number(record.refundAmount) > 0) {
        return {
            status: 'REFUNDED',
            label: 'Nie dotyczy (Zwrot)',
            badgeLabel: 'Zwrot',
            description: 'Zamówienie zostało zwrócone (refundowane). Wynik finansowy nieadekwatny ze względu na korektę przychodu.',
            color: '#bfbfbf', // light grey
            severity: 'info',
        };
    }

    const lines = record.lines || [];
    
    // Check if COGS is incomplete
    const hasMissingCogs = lines.length === 0 || lines.some((line: any) => {
        if (line.isShipping || line.sku === 'WYSYŁKA') return false;
        const cost = Number(line.unitCost ?? 0);
        return cost <= 0;
    });

    if (hasMissingCogs) {
        return {
            status: 'INCOMPLETE_COGS',
            label: 'Brak kosztu COGS ⚠️',
            badgeLabel: 'Brak COGS',
            description: 'Przynajmniej jeden produkt w tym zamówieniu nie ma zdefiniowanej ceny zakupu (COGS) w bazie danych.',
            color: '#faad14', // gold
            severity: 'warning',
        };
    }

    // Check channel type
    const channelName = record.channel?.name || '';
    const isAllegro = channelName.toLowerCase().includes('allegro') || !!record.externalId;

    let baseProfitability: OrderProfitability;

    if (isAllegro) {
        const hasDetailedFees = 
            Math.abs(Number(record.allegroCommission || 0)) > 0 ||
            Math.abs(Number(record.allegroPromotion || 0)) > 0 ||
            Math.abs(Number(record.allegroFulfillment || 0)) > 0 ||
            Math.abs(Number(record.allegroSmart || 0)) > 0 ||
            Math.abs(Number(record.allegroPayment || 0)) > 0 ||
            Math.abs(Number(record.allegroOther || 0)) > 0;

        if (!hasDetailedFees) {
            baseProfitability = {
                status: 'ESTIMATED_ALLEGRO',
                label: 'Oczekuje na opłaty ⏳',
                badgeLabel: 'Oczekuje opłaty',
                description: 'Zamówienie z Allegro. Prowizje i koszty przesyłki nie zostały jeszcze pobrane z API Allegro.',
                color: '#faad14', // gold
                severity: 'warning',
            };
        } else {
            baseProfitability = {
                status: 'FULL_DIRECT',
                label: 'Pewny (Pełne koszty)',
                badgeLabel: 'Pewny',
                description: 'Rzeczywisty wynik na zamówieniu uwzględniający koszt produktów (COGS) oraz prowizje, reklamy Allegro, dopłaty Smart i koszty wysyłki pobrane bezpośrednio z API Allegro.',
                color: '#52c41a', // green
                severity: 'success',
            };
        }
    } else {
        baseProfitability = {
            status: 'COGS_ONLY',
            label: 'Tylko COGS i wysyłka',
            badgeLabel: 'Tylko COGS',
            description: 'Wynik oparty na kosztach produktów (COGS) oraz ewentualnych ręcznie wprowadzonych opłatach. Prowizje bankowe, bramki płatnicze oraz realne koszty dostaw kurierskich nie są śledzone automatycznie dla tego kanału.',
            color: '#13c2c2', // cyan
            severity: 'info',
        };
    }

    // Now overlay payment check on top of it if the payment is missing/incomplete
    const billing = getBillingStatus(record);
    const isUnpaid = record.paymentStatus === 'PENDING' || record.paymentStatus === 'FAILED' || billing.label === 'Po terminie';
    
    if (isUnpaid) {
        return {
            status: 'UNPAID',
            label: `${baseProfitability.label} (Nieopłacone)`,
            badgeLabel: 'Nieopłacone',
            description: `Zamówienie nie zostało jeszcze opłacone (status płatności: ${billing.label}). Wynik ma charakter potencjalny i nie został zrealizowany.`,
            color: '#1890ff', // potential blue
            severity: 'warning',
        };
    }

    if (record.paymentStatus === 'PARTIALLY_PAID') {
        return {
            status: 'PARTIALLY_PAID',
            label: `${baseProfitability.label} (Część. opłacone)`,
            badgeLabel: 'Część. opłacone',
            description: `Zamówienie opłacone częściowo. Wynik finansowy zostanie w pełni zrealizowany po uregulowaniu całości kwoty.`,
            color: '#722ed1', // purple
            severity: 'info',
        };
    }

    return baseProfitability;
};

export default function OrdersPage() {
    const { token } = theme.useToken();
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const [status, setStatus] = useState<string | undefined>();
    const [billingStatus, setBillingStatus] = useState<string | undefined>();
    const [salesType, setSalesType] = useState<string | undefined>();
    const [channelId, setChannelId] = useState<string | undefined>();
    const [productId, setProductId] = useState<string | undefined>();
    const [search, setSearch] = useState<string | undefined>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [sortField, setSortField] = useState<string | undefined>('orderDate');
    const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC' | undefined>('DESC');
    const [tableDateRange, setTableDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<any>(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    useEffect(() => {
        setSelectedRowKeys([]);
    }, [status, billingStatus, salesType, channelId, search, page, pageSize, tableDateRange]);

    const [selectedLocationId, setSelectedLocationId] = useState<string | undefined>(); // For filtering products by warehouse
    const [statusForm] = Form.useForm();
    const [form] = Form.useForm();
    const [orderContactHistory, setOrderContactHistory] = useState<any[]>([]);
    const [isSaveFilterModalOpen, setIsSaveFilterModalOpen] = useState(false);
    const [saveFilterForm] = Form.useForm();

    const [filtersExpanded, setFiltersExpanded] = useState(false);

    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();

    // CRM Integration State
    const [selectedContractor, setSelectedContractor] = useState<Contractor | null>(null);
    const [selectedEditContractor, setSelectedEditContractor] = useState<Contractor | null>(null);

    // Check for contractorId query param on mount
    useEffect(() => {
        const contractorId = searchParams.get('contractorId');
        const createMode = searchParams.get('create');

        if (contractorId && createMode === 'true') {
            setIsModalOpen(true);
            // Fetch contractor details
            crmApi.getContractor(contractorId).then(response => {
                const contractor = response.data;
                if (contractor) {
                    handleContractorSelect(contractor.id, contractor);
                }
            }).catch(console.error);
        }
    }, [searchParams]);

    const handleContractorSelect = (value: string | undefined, contractor: Contractor | undefined) => {
        setSelectedContractor(contractor || null);

        if (contractor) {
            const primaryContact = contractor.contacts?.find(c => c.isPrimary) || contractor.contacts?.[0];
            const phone = primaryContact?.phone || undefined;

            // Auto-fill form
            form.setFieldsValue({
                customerName: contractor.name,
                contractorId: contractor.id,
                shippingName: contractor.name,
                shippingPhone: phone,
                shippingStreet: contractor.deliveryAddress?.street || contractor.invoiceAddress?.street,
                shippingCity: contractor.deliveryAddress?.city || contractor.invoiceAddress?.city,
                shippingPostalCode: contractor.deliveryAddress?.postalCode || contractor.invoiceAddress?.postalCode,
                shippingMethod: (contractor as any).defaultShippingMethod,
                paymentMethod: (contractor as any).defaultPaymentMethod,
                customerNote: contractor.fulfillmentNotes,
                // Add NIP to internal note if available (or separate field if we had one)
                internalNote: contractor.nip ? `NIP: ${contractor.nip}` : undefined
            });

            // Warn if blocked
            if (contractor.status === 'BLACKLIST') {
                message.warning('Ten kontrahent jest ZABLOKOWANY!');
            }
        } else {
            // Clear fields if deselected? Maybe not, user might want to keep typed data.
            // But let's clear at least the linked ID
            form.setFieldsValue({ contractorId: null });
        }
    };

    const handleEditContractorSelect = (value: string | undefined, contractor: Contractor | undefined) => {
        setSelectedEditContractor(contractor || null);

        if (contractor) {
            const primaryContact = contractor.contacts?.find(c => c.isPrimary) || contractor.contacts?.[0];
            const phone = primaryContact?.phone || undefined;
            // ... (auto-fill logic)
            editForm.setFieldsValue({
                contractorId: contractor.id,
                shippingName: contractor.name,
                shippingPhone: phone,
                shippingStreet: contractor.deliveryAddress?.street || contractor.invoiceAddress?.street,
                shippingCity: contractor.deliveryAddress?.city || contractor.invoiceAddress?.city,
                shippingPostalCode: contractor.deliveryAddress?.postalCode || contractor.invoiceAddress?.postalCode,
                shippingMethod: (contractor as any).defaultShippingMethod,
                paymentMethod: (contractor as any).defaultPaymentMethod,
                customerNote: contractor.fulfillmentNotes,
                internalNote: contractor.nip ? `NIP: ${contractor.nip}` : undefined
            });

            // Warn if blocked
            if (contractor.status === 'BLACKLIST') {
                message.warning('Ten kontrahent jest ZABLOKOWANY!');
            }
        } else {
            editForm.setFieldsValue({ contractorId: null });
        }
    };

    // Date range state - supports presets AND custom ranges
    const [datePreset, setDatePreset] = useState<'7d' | '30d' | 'mtd' | 'ytd' | 'custom'>('30d');
    const [customDateRange, setCustomDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

    // Charts section collapsed state (persisted)
    const [chartsExpanded, setChartsExpanded] = useState(() => {
        try {
            const saved = localStorage.getItem('orders-charts-expanded');
            // Default to false on mobile, otherwise check saved
            if (saved === null) return false;
            return saved === 'true';
        } catch (e) {
            console.warn('LocalStorage not available', e);
            return false;
        }
    });

    // KPI section collapsed state (persisted)
    const [kpisExpanded, setKpisExpanded] = useState(() => {
        try {
            const saved = localStorage.getItem('orders-kpis-expanded');
            if (saved === null) return false;
            return saved === 'true';
        } catch (e) {
            return false;
        }
    });

    // Calculate effective date range
    const getDateRange = () => {
        const now = dayjs();

        if (datePreset === 'custom' && customDateRange) {
            return {
                startDate: customDateRange[0].startOf('day').toISOString(),
                endDate: customDateRange[1].endOf('day').toISOString(),
                label: `${customDateRange[0].format('DD.MM.YYYY')} - ${customDateRange[1].format('DD.MM.YYYY')}`,
            };
        }

        switch (datePreset) {
            case '7d':
                return {
                    startDate: now.subtract(7, 'day').toISOString(),
                    endDate: now.toISOString(),
                    label: 'Ostatnie 7 dni',
                };
            case '30d':
                return {
                    startDate: now.subtract(30, 'day').toISOString(),
                    endDate: now.toISOString(),
                    label: 'Ostatnie 30 dni',
                };
            case 'mtd':
                return {
                    startDate: now.startOf('month').toISOString(),
                    endDate: now.toISOString(),
                    label: `${now.format('MMMM YYYY')} (MTD)`,
                };
            case 'ytd':
                return {
                    startDate: now.startOf('year').toISOString(),
                    endDate: now.toISOString(),
                    label: `${now.year()} (YTD)`,
                };
            default:
                return {
                    startDate: now.subtract(30, 'day').toISOString(),
                    endDate: now.toISOString(),
                    label: 'Ostatnie 30 dni',
                };
        }
    };
    const dateRange = getDateRange();

    const { data: ordersData, isLoading } = useQuery({
        queryKey: ['orders', status, billingStatus, salesType, channelId, search, page, pageSize, sortField, sortOrder, tableDateRange?.[0]?.startOf('day').toISOString(), tableDateRange?.[1]?.endOf('day').toISOString(), productId],
        queryFn: () => ordersApi.getAll({
            status, channelId, search, page, limit: pageSize, sortField, sortOrder,
            startDate: tableDateRange?.[0] ? tableDateRange[0].startOf('day').toISOString() : undefined,
            endDate: tableDateRange?.[1] ? tableDateRange[1].endOf('day').toISOString() : undefined,
            productId,
            billingStatus,
            salesType,
        }).then((res) => res.data),
    });

    const { data: facetsData } = useQuery({
        queryKey: ['orders-facets', status, billingStatus, salesType, channelId, search, tableDateRange?.[0]?.startOf('day').toISOString(), tableDateRange?.[1]?.endOf('day').toISOString(), productId],
        queryFn: () => ordersApi.getFacets({
            status,
            channelId,
            search,
            startDate: tableDateRange?.[0] ? tableDateRange[0].startOf('day').toISOString() : undefined,
            endDate: tableDateRange?.[1] ? tableDateRange[1].endOf('day').toISOString() : undefined,
            billingStatus,
            salesType,
            productId,
        }).then((res) => res.data),
    });

    // KPI query - same filters as table but for dashboard stats (keep global dateRange)
    const { data: kpiData } = useQuery({
        queryKey: ['orders-kpi', status, channelId, search, datePreset, customDateRange?.[0]?.toISOString(), customDateRange?.[1]?.toISOString(), productId],
        queryFn: () => ordersApi.getStats({
            status, channelId, search,
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            productId,
        }).then((res) => res.data),
    });

    // Chart data queries
    const { data: dailySalesData } = useQuery({
        queryKey: ['daily-sales', datePreset, customDateRange?.[0]?.toISOString(), customDateRange?.[1]?.toISOString(), channelId],
        queryFn: () => ordersApi.getDailySales({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            channelId,
        }).then((res) => res.data),
        enabled: chartsExpanded, // Only fetch when charts are visible
    });

    // Calculate previous period dates (same length, immediately before current period)
    const getPreviousPeriodDates = () => {
        const start = dayjs(dateRange.startDate);
        const end = dayjs(dateRange.endDate);
        const periodDays = end.diff(start, 'day') + 1;
        return {
            startDate: start.subtract(periodDays, 'day').toISOString(),
            endDate: start.subtract(1, 'day').endOf('day').toISOString(),
        };
    };
    const previousPeriod = getPreviousPeriodDates();

    // Fetch previous period data for trend comparison
    const { data: previousPeriodData } = useQuery({
        queryKey: ['daily-sales-prev', datePreset, customDateRange?.[0]?.toISOString(), customDateRange?.[1]?.toISOString(), channelId],
        queryFn: () => ordersApi.getDailySales({
            startDate: previousPeriod.startDate,
            endDate: previousPeriod.endDate,
            channelId,
        }).then((res) => res.data),
        enabled: chartsExpanded,
    });

    const { data: productBreakdownData } = useQuery({
        queryKey: ['product-breakdown', datePreset, customDateRange?.[0]?.toISOString(), customDateRange?.[1]?.toISOString(), channelId],
        queryFn: () => ordersApi.getProductBreakdown({
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            channelId,
            metric: 'revenue',
        }).then((res) => res.data),
        enabled: chartsExpanded,
    });

    // Trend calculation: current period vs previous period
    const trendData = dailySalesData && dailySalesData.length > 0 ? (() => {
        const currentTotal = dailySalesData.reduce((sum: number, d: any) => sum + d.revenue, 0);
        const currentDays = dailySalesData.length;
        const avgCurrent = currentTotal / Math.max(currentDays, 1);

        // Use previous period if available
        const prevTotal = previousPeriodData?.reduce((sum: number, d: any) => sum + d.revenue, 0) || 0;
        const prevDays = previousPeriodData?.length || currentDays;
        const avgPrev = prevTotal / Math.max(prevDays, 1);

        // Calculate change - handle cases where previous is 0
        let change = 0;
        if (avgPrev > 0) {
            change = ((avgCurrent - avgPrev) / avgPrev) * 100;
        } else if (avgCurrent > 0) {
            // Previous was 0, current has sales = 100% growth (or show as "new")
            change = 100;
        }
        // If both are 0, change stays 0

        return {
            avgCurrent,  // Current period avg
            avgPrev,     // Previous period avg
            change,
        };
    })() : null;

    const { data: channelsData } = useQuery({
        queryKey: ['channels'],
        queryFn: () => ordersApi.getChannels().then((res: any) => res.data),
    });

    const { data: productsData } = useQuery({
        queryKey: ['products-for-orders'],
        queryFn: () => productsApi.getAll({}).then((res: any) => res.data),
    });

    // Get warehouse locations
    const { data: locationsData } = useQuery({
        queryKey: ['locations-for-orders'],
        queryFn: () => inventoryApi.getLocations().then((res: any) => res.data),
    });

    // Get stock levels for all products
    const { data: stockData } = useQuery({
        queryKey: ['stock-for-orders'],
        queryFn: () => inventoryApi.getStock({}).then((res: any) => res.data),
    });

    // Saved filters
    const { data: savedFiltersData } = useQuery({
        queryKey: ['user-filters', 'orders'],
        queryFn: () => usersApi.getFilters('orders').then((res: any) => res.data),
    });

    const createFilterMutation = useMutation({
        mutationFn: (data: { name: string; page: string; filters: Record<string, any> }) =>
            usersApi.createFilter(data),
        onSuccess: () => {
            message.success('Filtr zapisany');
            queryClient.invalidateQueries({ queryKey: ['user-filters'] });
            setIsSaveFilterModalOpen(false);
            saveFilterForm.resetFields();
        },
    });

    const deleteFilterMutation = useMutation({
        mutationFn: (filterId: string) => usersApi.deleteFilter(filterId),
        onSuccess: () => {
            message.success('Filtr usunięty');
            queryClient.invalidateQueries({ queryKey: ['user-filters'] });
        },
    });

    const handleApplyFilter = (filter: any) => {
        if (filter.filters.status !== undefined) setStatus(filter.filters.status);
    };

    const handleSaveFilter = (values: { name: string }) => {
        createFilterMutation.mutate({
            name: values.name,
            page: 'orders',
            filters: { status },
        });
    };

    // Create a map of locationId -> productId -> available stock
    // This allows filtering products based on selected warehouse
    const stockByLocation: Record<string, Record<string, number>> = {};
    const stockMapAll: Record<string, number> = {}; // Total stock across all locations
    if (stockData) {
        stockData.forEach((s: any) => {
            // Only count stock that has a location (real warehouse stock)
            if (s.locationId && s.available > 0) {
                if (!stockByLocation[s.locationId]) {
                    stockByLocation[s.locationId] = {};
                }
                stockByLocation[s.locationId][s.productId] = (stockByLocation[s.locationId][s.productId] || 0) + s.available;
                stockMapAll[s.productId] = (stockMapAll[s.productId] || 0) + s.available;
            }
        });
    }

    // Get stock for selected location (or all if no location selected)
    const getStockForProduct = (productId: string): number => {
        if (selectedLocationId && stockByLocation[selectedLocationId]) {
            return stockByLocation[selectedLocationId][productId] || 0;
        }
        return stockMapAll[productId] || 0;
    };

    const createOrderMutation = useMutation({
        mutationFn: (data: any) => ordersApi.create(data).then((res) => res.data),
        onSuccess: (newOrder: any) => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['stock-for-orders'] });
            queryClient.invalidateQueries({ queryKey: ['batches'] });
            queryClient.invalidateQueries({ queryKey: ['stock'] });
            queryClient.invalidateQueries({ queryKey: ['events'] });
            queryClient.invalidateQueries({ queryKey: ['batchStockByLocation'] });

            // Check if the new order is visible in the current date range
            const orderDate = dayjs(newOrder.orderDate || newOrder.createdAt);
            const start = dayjs(dateRange.startDate);
            const end = dayjs(dateRange.endDate);

            if (orderDate.isBefore(start) || orderDate.isAfter(end)) {
                // Auto-expand date range
                const newStart = orderDate.isBefore(start) ? orderDate : start;
                const newEnd = orderDate.isAfter(end) ? orderDate : end;

                setCustomDateRange([newStart, newEnd]);
                setDatePreset('custom');

                message.success('Zamówienie utworzone');
                message.info('Zakres dat został automatycznie rozszerzony, aby pokazać nowe zamówienie.');
            } else {
                message.success('Zamówienie utworzone');
            }

            setIsModalOpen(false);
            form.resetFields();
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd tworzenia zamówienia');
        },
    });

    const updateStatusMutation = useMutation({
        mutationFn: ({ id, status, reason, shippedAt, deliveredAt }: { id: string; status: string; reason?: string; shippedAt?: string; deliveredAt?: string }) =>
            ordersApi.updateStatus(id, status, reason, shippedAt, deliveredAt).then((res: any) => res.data),
        onSuccess: (updatedOrder: any) => {
            // Invalidate all order-related queries including charts and KPIs
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['orders-kpi'] });
            queryClient.invalidateQueries({ queryKey: ['daily-sales'] });
            queryClient.invalidateQueries({ queryKey: ['daily-sales-prev'] });
            queryClient.invalidateQueries({ queryKey: ['product-breakdown'] });
            message.success('Status zamówienia zaktualizowany');
            setIsStatusModalOpen(false);
            // Keep detail modal open and update selected order with fresh data
            if (updatedOrder) {
                setSelectedOrder(updatedOrder);
            }
            statusForm.resetFields();
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd aktualizacji statusu');
        },
    });

    const bulkUpdateStatusMutation = useMutation({
        mutationFn: async ({ ids, status }: { ids: string[]; status: string }) => {
            const promises = ids.map(id => ordersApi.updateStatus(id, status).then((res: any) => res.data));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            queryClient.invalidateQueries({ queryKey: ['orders-kpi'] });
            queryClient.invalidateQueries({ queryKey: ['daily-sales'] });
            queryClient.invalidateQueries({ queryKey: ['daily-sales-prev'] });
            queryClient.invalidateQueries({ queryKey: ['product-breakdown'] });
            message.success('Statusy zamówień zaktualizowane zbiorczo');
            setSelectedRowKeys([]);
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd zbiorczej aktualizacji statusów');
        }
    });

    const handleBulkExport = () => {
        const selectedOrders = (orders || []).filter((o: any) => selectedRowKeys.includes(o.id));
        if (selectedOrders.length === 0) return;

        const headers = [
            'Nr zamówienia',
            'Data',
            'Klient',
            'Produkty',
            'Email',
            'Status',
            'Kanał',
            'Suma',
            'Zysk',
            'Nr przesyłki',
        ];

        const rows = selectedOrders.map((o: any) => [
            o.orderNumber || '',
            o.orderDate ? dayjs(o.orderDate).format('DD.MM.YYYY') : '',
            o.customerName || (o.customer ? `${o.customer.firstName || ''} ${o.customer.lastName || ''}` : ''),
            groupOrderLinesForDisplay(o.lines).map((line) => `${line.quantity}x ${line.name}`).join(', '),
            o.customerEmail || o.customer?.email || '',
            statusLabels[o.status] || o.status,
            o.channel?.name || 'Ręczne',
            o.total || 0,
            o.grossProfit || 0,
            o.trackingNumber || '',
        ]);

        let csv = headers.join(';') + '\n';
        for (const row of rows) {
            csv += row.map((v: any) => `"${String(v).replace(/"/g, '""')}"`).join(';') + '\n';
        }

        const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `zamowienia_zaznaczone_${dayjs().format('YYYYMMDD')}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        message.success(`Wyeksportowano ${selectedOrders.length} zaznaczonych zamówień`);
    };

    const handleBulkStatusUpdate = (newStatus: string) => {
        const selectedOrders = (orders || []).filter((o: any) => selectedRowKeys.includes(o.id));
        const hasAllegroOrders = selectedOrders.some((o: any) => o.channel?.name?.toLowerCase().includes('allegro'));

        Modal.confirm({
            title: 'Zbiorcza zmiana statusu',
            content: (
                <div>
                    <p>Czy na pewno chcesz zmienić status dla <strong>{selectedRowKeys.length}</strong> zaznaczonych zamówień na <strong>{statusLabels[newStatus] || newStatus}</strong>?</p>
                    {hasAllegroOrders && (
                        <div style={{
                            marginTop: 12,
                            padding: '8px 12px',
                            backgroundColor: '#fffbe6',
                            border: '1px solid #ffe58f',
                            borderRadius: 4,
                            color: 'rgba(0,0,0,0.85)',
                            fontSize: 12
                        }}>
                            <span style={{ fontWeight: 600, color: '#d46b08' }}>⚠ Uwaga: Zamówienia z Allegro</span>
                            <div style={{ marginTop: 4 }}>
                                Wybrane zamówienia zawierają pozycje z Allegro. Zmiana ich statusu w systemie ERP może nie zaktualizować statusu automatycznie na portalu Allegro.
                            </div>
                        </div>
                    )}
                </div>
            ),
            okText: 'Tak, zmień',
            cancelText: 'Anuluj',
            okType: 'primary',
            onOk: async () => {
                bulkUpdateStatusMutation.mutate({ ids: selectedRowKeys as string[], status: newStatus });
            }
        });
    };

    // Edit order mutation
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editForm] = Form.useForm();
    const updateOrderMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: any }) => ordersApi.update(id, data).then((res) => res.data),
        onSuccess: (updatedOrder: any) => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });

            // Check if the updated order is visible in the current date range
            const orderDate = dayjs(updatedOrder.orderDate || updatedOrder.createdAt);
            const start = dayjs(dateRange.startDate);
            const end = dayjs(dateRange.endDate);

            if (orderDate.isBefore(start) || orderDate.isAfter(end)) {
                // Auto-expand date range
                const newStart = orderDate.isBefore(start) ? orderDate : start;
                const newEnd = orderDate.isAfter(end) ? orderDate : end;

                setCustomDateRange([newStart, newEnd]);
                setDatePreset('custom');

                message.success('Zamówienie zaktualizowane');
                message.info('Zakres dat został automatycznie rozszerzony, aby pokazać zaktualizowane zamówienie.');
            } else {
                message.success('Zamówienie zaktualizowane');
            }

            setIsEditModalOpen(false);
            setSelectedOrder(null);
            setIsDetailModalOpen(false);
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd aktualizacji zamówienia');
        },
    });

    // Delete order mutation
    const deleteOrderMutation = useMutation({
        mutationFn: (id: string) => ordersApi.delete(id).then((res) => res.data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['orders'] });
            message.success('Zamówienie usunięte');
            setSelectedOrder(null);
            setIsDetailModalOpen(false);
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Błąd usuwania zamówienia');
        },
    });

    const handleDeleteOrder = () => {
        if (selectedOrder) {
            Modal.confirm({
                title: 'Usunąć zamówienie?',
                content: `Zamówienie ${getShortOrderNumber(selectedOrder)} zostanie trwale usunięte.`,
                okText: 'Usuń',
                okType: 'danger',
                cancelText: 'Anuluj',
                onOk: () => deleteOrderMutation.mutate(selectedOrder.id),
            });
        }
    };

    const handleEditOrder = () => {
        if (selectedOrder) {
            setSelectedEditContractor(selectedOrder.contractor || null);
            editForm.setFieldsValue({
                orderDate: selectedOrder.orderDate ? dayjs(selectedOrder.orderDate) : undefined,
                contractorId: selectedOrder.contractorId,
                shippingName: selectedOrder.shippingName,
                shippingPhone: selectedOrder.shippingPhone,
                shippingStreet: selectedOrder.shippingStreet,
                shippingCity: selectedOrder.shippingCity,
                shippingPostalCode: selectedOrder.shippingPostalCode,
                shippingMethod: selectedOrder.shippingMethod,
                trackingNumber: selectedOrder.trackingNumber,
                paymentMethod: selectedOrder.paymentMethod,
                paymentStatus: selectedOrder.paymentStatus,
                paidAmount: selectedOrder.paidAmount,
                refundAmount: selectedOrder.refundAmount,
                goodsReturned: selectedOrder.goodsReturned,
                customerNote: selectedOrder.customerNote,
                internalNote: selectedOrder.internalNote,
                customFields: {
                    ...(selectedOrder.customFields || {}),
                    salesType: selectedOrder.customFields?.salesType === 'allegro' ? 'detal' :
                               selectedOrder.customFields?.salesType === 'direct_sale' ? 'detal' :
                               selectedOrder.customFields?.salesType || (selectedOrder.contractorId ? 'b2b' : 'detal')
                },
            });
            setIsEditModalOpen(true);
        }
    };

    const handleEditSubmit = (values: any) => {
        if (selectedOrder) {
            // Automatically set status to REFUNDED when refundAmount is entered
            const { orderDate, ...restValues } = values;
            const updatedValues = {
                ...restValues,
                orderDate: orderDate ? orderDate.toISOString() : undefined,
            };
            if (Number(values.refundAmount) > 0 && selectedOrder.status !== 'REFUNDED') {
                updatedValues.status = 'REFUNDED';
            }
            updateOrderMutation.mutate({ id: selectedOrder.id, data: updatedValues });
        }
    };

    const handleStatusChange = (values: { status: string; reason?: string; statusDate?: any }) => {
        if (selectedOrder) {
            // Convert dayjs date to ISO string if provided
            const statusDateIso = values.statusDate ? values.statusDate.toISOString() : undefined;

            const mutationData: any = {
                id: selectedOrder.id,
                status: values.status,
                reason: values.reason,
            };

            // Set the appropriate date field based on status
            if (values.status === 'SHIPPED') {
                mutationData.shippedAt = statusDateIso;
            } else if (values.status === 'DELIVERED') {
                mutationData.deliveredAt = statusDateIso;
            }

            updateStatusMutation.mutate(mutationData);
        }
    };

    const handleSubmit = (values: any) => {
        // Calculate totals
        const items = values.items || [];
        const subtotal = items.reduce((sum: number, item: any) => {
            return sum + (item?.quantity || 0) * (item?.unitPrice || 0);
        }, 0);

        // Remove redundant items property from values
        const { items: _, orderDate, ...restValues } = values;

        createOrderMutation.mutate({
            ...restValues,
            orderDate: orderDate ? orderDate.toISOString() : undefined,
            subtotal,
            total: subtotal + (values.shippingAmount || 0),
            lines: items.map((item: any) => ({
                productId: item.productId,
                quantity: item.quantity,
                unitPrice: item.unitPrice,
                totalPrice: item.quantity * item.unitPrice,
            })),
        });
    };


    const columns = [
        {
            title: 'Nr zamówienia (ERP)',
            key: 'orderNumber',
            width: 160,
            render: (_: any, record: any) => {
                const shortNum = getShortOrderNumber(record);
                const originalNum = record.orderNumber || '';
                return (
                    <Tooltip title={originalNum ? `Zewnętrzny ID: ${originalNum}` : 'Brak zewnętrznego ID'}>
                        <span style={{ 
                            fontWeight: 'bold', 
                            fontSize: '13px', 
                            color: token.colorPrimary,
                            letterSpacing: '0.3px',
                            cursor: 'help'
                        }}>
                            {shortNum}
                        </span>
                    </Tooltip>
                );
            },
            sorter: (a: any, b: any) => (a.orderNumber || '').localeCompare(b.orderNumber || ''),
        },
        {
            title: 'Klient',
            key: 'customer',
            render: (_: any, record: any) => {
                const name = record.customerName ||
                    (record.customer
                        ? `${record.customer.firstName || ''} ${record.customer.lastName || ''}`
                        : record.shippingName || '-');
                const type = getSalesType(record);
                const isB2B = type.label === 'B2B';
                const hasCrm = isB2B && !!record.contractorId;
                return (
                    <Space direction="vertical" size={2}>
                        <span style={{ fontWeight: 500, fontSize: 13 }}>{name}</span>
                        {hasCrm ? (
                            <Tooltip title="Otwórz kartę firmy w CRM" placement="right">
                                <a
                                    href={`/b2b/${record.contractorId}`}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                        fontSize: 11,
                                        color: token.colorPrimary,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        opacity: 0.85,
                                    }}
                                >
                                    <BankOutlined style={{ fontSize: 11 }} />
                                    Karta firmy
                                </a>
                            </Tooltip>
                        ) : isB2B ? (
                            <span style={{ fontSize: 11, color: token.colorWarning }}>B2B (brak CRM)</span>
                        ) : (
                            <span style={{ fontSize: 11, color: token.colorTextDescription }}>
                                {type.label}
                            </span>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Produkty',
            key: 'products',
            width: 240,
            render: (_: any, record: any) => (
                <OrderProductsCell lines={record.lines} maxVisible={2} variant="table" />
            ),
        },
        {
            title: 'Płatność',
            key: 'billingStatus',
            width: 125,
            render: (_: any, record: any) => {
                const billing = getBillingStatus(record);
                return (
                    <Tag
                        color={billing.color}
                        style={{
                            fontWeight: billing.problem ? 700 : 400,
                            borderStyle: billing.problem ? 'solid' : undefined,
                        }}
                    >
                        {billing.label}
                    </Tag>
                );
            },
            sorter: (a: any, b: any) => (a.paymentStatus || '').localeCompare(b.paymentStatus || ''),
        },
        {
            title: 'Realizacja',
            key: 'shipping',
            width: 180,
            render: (_: any, record: any) => {
                const colors: Record<string, string> = {
                    PENDING: 'gold',
                    CONFIRMED: 'blue',
                    PROCESSING: 'cyan',
                    PACKED: 'purple',
                    SHIPPED: 'geekblue',
                    DELIVERED: 'green',
                    COMPLETED: 'green',
                    CANCELLED: 'red',
                    REFUNDED: 'red',
                    ON_HOLD: 'orange',
                };
                const problemStatuses = new Set(['CANCELLED', 'REFUNDED', 'ON_HOLD']);
                const label = statusLabels[record.status] || record.status;
                const isProblem = problemStatuses.has(record.status);
                const trackingUrl = record.trackingNumber ? getTrackingUrl(record.trackingNumber, record.shippingMethod) : null;
                const shipment = getShipmentStatus(record);

                return (
                    <Space size={6} align="center">
                        {/* Order status tag */}
                        <Tag
                            color={colors[record.status] || 'default'}
                            style={{ margin: 0, fontWeight: isProblem ? 700 : 400 }}
                        >
                            {label}
                        </Tag>
                        {trackingUrl && (
                            <Tooltip title={`Śledź przesyłkę: ${record.trackingNumber} (${record.shippingMethod || 'Kurier'})`}>
                                <a
                                    href={trackingUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ display: 'inline-flex', alignItems: 'center' }}
                                >
                                    <LinkOutlined style={{ fontSize: 13, color: token.colorPrimary }} />
                                </a>
                            </Tooltip>
                        )}
                    </Space>
                );
            },
            sorter: (a: any, b: any) => (a.status || '').localeCompare(b.status || ''),
        },
        {
            title: 'Wartość',
            dataIndex: 'total',
            key: 'total',
            width: 110,
            render: (val: any) =>
                new Intl.NumberFormat('pl-PL', {
                    style: 'currency',
                    currency: 'PLN',
                }).format(Number(val || 0)),
            sorter: (a: any, b: any) => Number(a.total || 0) - Number(b.total || 0),
        },
        {
            title: 'Data',
            dataIndex: 'orderDate',
            key: 'orderDate',
            render: (date: string, record: any) => (
                <span style={{ fontSize: 12, color: token.colorTextSecondary }}>
                    {dayjs(date || record.createdAt).format('DD.MM.YY')}
                    <br />
                    <span style={{ fontSize: 11, color: token.colorTextDescription }}>
                        {dayjs(date || record.createdAt).format('HH:mm')}
                    </span>
                </span>
            ),
            width: 90,
            sorter: (a: any, b: any) => new Date(a.orderDate || a.createdAt).getTime() - new Date(b.orderDate || b.createdAt).getTime(),
            defaultSortOrder: 'descend' as const,
        },
        {
            title: 'Kanał',
            key: 'channelType',
            width: 120,
            render: (_: any, record: any) => {
                const channelName = record.channel?.name || 'Ręczne';
                return (
                    <Space direction="vertical" size={0}>
                        <Tag style={{ margin: 0, fontSize: 11 }}>{channelName}</Tag>
                    </Space>
                );
            },
        },
        {
            title: (
                <Space>
                    Wynik
                    <Tooltip title="Wynik finansowy (przychód minus COGS i bezpośrednie opłaty platformy). Kolor wskaźnika oznacza stopień pewności danych: Zielony (Pełny), Turkusowy (Tylko COGS), Pomarańczowy (Brak COGS lub czekający na synchronizację opłat).">
                        <InfoCircleOutlined style={{ color: '#999', fontSize: 12 }} />
                    </Tooltip>
                </Space>
            ),
            key: 'directProfit',
            width: 140,
            render: (_: any, record: any) => {
                const isCancelled = record.status === 'CANCELLED';
                const isRefunded = record.status === 'REFUNDED' || record.paymentStatus === 'REFUNDED' || Number(record.refundAmount) > 0;
                const isZeroProfit = isCancelled || isRefunded;
                const profit = isZeroProfit ? 0 : (record.netProfit !== null && record.netProfit !== undefined 
                    ? Number(record.netProfit) 
                    : Number(record.grossProfit || 0));
                const margin = !isZeroProfit && record.total > 0 ? (profit / Number(record.total)) * 100 : 0;
                const profitability = getOrderProfitability(record);
                const isUnreliable = ['INCOMPLETE_COGS', 'ESTIMATED_ALLEGRO', 'UNPAID', 'PARTIALLY_PAID'].includes(profitability.status);
                
                let profitColor = '#52c41a'; // green
                if (isZeroProfit) {
                    profitColor = token.colorTextDescription; // grey
                } else if (profitability.status === 'UNPAID') {
                    profitColor = '#1890ff'; // potential blue
                } else if (profitability.status === 'PARTIALLY_PAID') {
                    profitColor = '#722ed1'; // purple
                } else if (profitability.status === 'INCOMPLETE_COGS' || profitability.status === 'ESTIMATED_ALLEGRO') {
                    profitColor = '#d46b08'; // warning orange
                } else if (profit < 0) {
                    profitColor = '#ff4d4f'; // red
                }

                return (
                    <Tooltip title={
                        <div style={{ fontSize: 12 }}>
                            <strong>{profitability.label}</strong>
                            <p style={{ margin: '4px 0 0 0' }}>{profitability.description}</p>
                        </div>
                    }>
                        <Space direction="vertical" size={0} style={{ cursor: 'pointer' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ fontWeight: isZeroProfit ? '400' : '600', fontSize: 12, color: profitColor }}>
                                    {isUnreliable ? '~ ' : ''}{new Intl.NumberFormat('pl-PL', {
                                        style: 'currency',
                                        currency: 'PLN',
                                    }).format(profit)}
                                </span>
                                <Badge color={profitability.color} style={{ transform: 'scale(0.8)' }} />
                            </div>
                            <span style={{ fontSize: 11, color: token.colorTextDescription }}>
                                {isZeroProfit ? '—' : `${isUnreliable ? '~ ' : ''}${margin.toFixed(1)}%`} <span style={{ fontSize: 9, color: profitability.color }}>({profitability.badgeLabel})</span>
                            </span>
                        </Space>
                    </Tooltip>
                );
            },
            sorter: (a: any, b: any) => {
                const aIsZero = a.status === 'CANCELLED' || a.status === 'REFUNDED' || a.paymentStatus === 'REFUNDED' || Number(a.refundAmount) > 0;
                const bIsZero = b.status === 'CANCELLED' || b.status === 'REFUNDED' || b.paymentStatus === 'REFUNDED' || Number(b.refundAmount) > 0;
                const aProfit = aIsZero ? 0 : (a.netProfit !== null && a.netProfit !== undefined ? Number(a.netProfit) : Number(a.grossProfit || 0));
                const bProfit = bIsZero ? 0 : (b.netProfit !== null && b.netProfit !== undefined ? Number(b.netProfit) : Number(b.grossProfit || 0));
                return aProfit - bProfit;
            },
        },
        {
            title: '',
            key: 'notes',
            width: 32,
            render: (_: any, record: any) => (
                (record.customerNote || record.internalNote) ? (
                    <Tooltip title={
                        <div>
                            {record.customerNote && <div><strong>Klient:</strong> {record.customerNote}</div>}
                            {record.internalNote && <div><strong>Wewn:</strong> {record.internalNote}</div>}
                        </div>
                    }>
                        <MessageOutlined style={{ color: '#faad14' }} />
                    </Tooltip>
                ) : null
            ),
        },
    ];

    const orders = ordersData?.data || [];
    // Use server-side calculated KPIs (independent of pagination/sorting)
    const totalRevenue = Number(kpiData?.totalRevenue || 0);
    const totalProfit = Number(kpiData?.totalProfit || 0);
    const totalOrders = Number(kpiData?.totalOrders || 0);

    const rawChannelOptions = (channelsData || [])
        .filter((c: any) => c.name !== 'YouTube')
        .map((c: any) => ({
            label: c.name,
            value: c.id,
        }));

    // Deduplicate by name (label) to fix production issue with duplicate channels
    const channelOptions: any[] = Array.from(
        new Map(rawChannelOptions.map((item: any) => [item.label, item])).values()
    );

    // Only show warehouse-type locations for shipping
    const locationOptions = (locationsData || [])
        .filter((l: any) => l.type === 'WAREHOUSE')
        .map((l: any) => ({
            label: l.name,
            value: l.id,
        }));

    const productOptions = (productsData?.data || [])
        .filter((p: any) => getStockForProduct(p.id) > 0) // Only show products with stock in selected location
        .map((p: any) => {
            const stock = getStockForProduct(p.id);
            return {
                label: `${p.sku} - ${p.name}`,
                value: p.id,
                price: p.basePrice,
                stock,
            };
        });

    // Period selector label helper
    const getPeriodLabel = (preset: string) => {
        const now = dayjs();
        switch (preset) {
            case '7d': return 'Ostatnie 7 dni';
            case '30d': return 'Ostatnie 30 dni';
            case 'mtd': return `${now.format('MMMM YYYY')} MTD`;
            case 'ytd': return `${now.year()} YTD`;
            case 'custom': return 'Własny zakres';
            default: return 'Ostatnie 30 dni';
        }
    };

    // Count active filters for badge
    const activeFiltersCount = [
        billingStatus,
        status,
        channelId,
        salesType,
        tableDateRange?.[0],
        search,
        productId,
    ].filter(Boolean).length;

    const clearAllFilters = () => {
        setBillingStatus(undefined);
        setStatus(undefined);
        setChannelId(undefined);
        setSalesType(undefined);
        setTableDateRange(null);
        setSearch(undefined);
        setProductId(undefined);
        setPage(1);
    };

    // Facet disabled helpers
    const isStatusDisabled = (val: string) => {
        if (!facetsData || !facetsData.statuses) return false;
        if (status === val) return false;
        return !facetsData.statuses.includes(val);
    };

    const isBillingStatusDisabled = (val: string) => {
        if (!facetsData || !facetsData.billingStatuses) return false;
        if (billingStatus === val) return false;
        return !facetsData.billingStatuses.includes(val);
    };

    const isChannelDisabled = (val: string) => {
        if (!facetsData || !facetsData.channels) return false;
        if (channelId === val) return false;
        const channel = (channelsData || []).find((c: any) => c.id === val);
        if (!channel) return true;
        const activeChannelNames = (facetsData.channels as string[]).map(id => {
            const ch = (channelsData || []).find((c: any) => c.id === id);
            return ch?.name;
        }).filter(Boolean);
        return !activeChannelNames.includes(channel.name);
    };

    const isSalesTypeDisabled = (val: string) => {
        if (!facetsData || !facetsData.salesTypes) return false;
        if (salesType === val) return false;
        return !facetsData.salesTypes.includes(val);
    };

    const isProductDisabled = (val: string) => {
        if (!facetsData || !facetsData.products) return false;
        if (productId === val) return false;
        return !facetsData.products.includes(val);
    };

    const getActivePreset = () => {
        if (
            status === 'PROCESSING' &&
            billingStatus === undefined &&
            salesType === undefined &&
            tableDateRange === null &&
            productId === undefined &&
            search === undefined
        ) {
            return 'to_fulfill';
        }

        if (
            status === undefined &&
            billingStatus === 'OVERDUE' &&
            salesType === undefined &&
            tableDateRange === null &&
            productId === undefined &&
            search === undefined
        ) {
            return 'payment_issues';
        }

        if (
            status === undefined &&
            billingStatus === undefined &&
            salesType === 'b2b' &&
            tableDateRange === null &&
            productId === undefined &&
            search === undefined
        ) {
            return 'b2b';
        }

        if (
            status === undefined &&
            billingStatus === undefined &&
            salesType === undefined &&
            productId === undefined &&
            search === undefined &&
            tableDateRange !== null &&
            tableDateRange[0] &&
            tableDateRange[1]
        ) {
            const diffDays = tableDateRange[1].diff(tableDateRange[0], 'day');
            const diffFromNow = dayjs().diff(tableDateRange[1], 'day');
            if (diffDays >= 6 && diffDays <= 8 && diffFromNow <= 1) {
                return 'last_7_days';
            }
        }

        if (
            status === undefined &&
            billingStatus === undefined &&
            salesType === undefined &&
            tableDateRange === null &&
            productId === undefined &&
            search === undefined
        ) {
            return 'all';
        }

        return 'custom';
    };

    const applyPreset = (presetId: string) => {
        setPage(1);
        if (presetId === 'all') {
            setStatus(undefined);
            setBillingStatus(undefined);
            setSalesType(undefined);
            setTableDateRange(null);
            setProductId(undefined);
            setSearch(undefined);
        } else if (presetId === 'to_fulfill') {
            setStatus('PROCESSING');
            setBillingStatus(undefined);
            setSalesType(undefined);
            setTableDateRange(null);
            setProductId(undefined);
            setSearch(undefined);
        } else if (presetId === 'payment_issues') {
            setStatus(undefined);
            setBillingStatus('OVERDUE');
            setSalesType(undefined);
            setTableDateRange(null);
            setProductId(undefined);
            setSearch(undefined);
        } else if (presetId === 'b2b') {
            setStatus(undefined);
            setBillingStatus(undefined);
            setSalesType('b2b');
            setTableDateRange(null);
            setProductId(undefined);
            setSearch(undefined);
        } else if (presetId === 'last_7_days') {
            setStatus(undefined);
            setBillingStatus(undefined);
            setSalesType(undefined);
            setTableDateRange([dayjs().subtract(7, 'day').startOf('day'), dayjs().endOf('day')]);
            setProductId(undefined);
            setSearch(undefined);
        }
    };

    const activePreset = getActivePreset();

    const presetElements = (
        <div
            style={{
                marginBottom: 16,
                padding: '8px 12px',
                background: token.colorFillAlter,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 8,
                border: `1px solid ${token.colorBorderSecondary}`
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: token.colorTextDescription, marginRight: 8 }}>
                    Szybkie widoki:
                </span>
                <Space wrap size={6}>
                    <Button
                        size="small"
                        type={activePreset === 'all' ? 'primary' : 'default'}
                        onClick={() => applyPreset('all')}
                        style={{ fontSize: 12, borderRadius: 4 }}
                    >
                        Wszystkie
                    </Button>
                    <Button
                        size="small"
                        type={activePreset === 'to_fulfill' ? 'primary' : 'default'}
                        onClick={() => applyPreset('to_fulfill')}
                        style={{ fontSize: 12, borderRadius: 4 }}
                    >
                        Do realizacji
                    </Button>
                    <Button
                        size="small"
                        type={activePreset === 'payment_issues' ? 'primary' : 'default'}
                        onClick={() => applyPreset('payment_issues')}
                        style={{ fontSize: 12, borderRadius: 4 }}
                    >
                        Problemy z płatnością
                    </Button>
                    <Button
                        size="small"
                        type={activePreset === 'b2b' ? 'primary' : 'default'}
                        onClick={() => applyPreset('b2b')}
                        style={{ fontSize: 12, borderRadius: 4 }}
                    >
                        B2B
                    </Button>
                    <Button
                        size="small"
                        type={activePreset === 'last_7_days' ? 'primary' : 'default'}
                        onClick={() => applyPreset('last_7_days')}
                        style={{ fontSize: 12, borderRadius: 4 }}
                    >
                        Ostatnie 7 dni
                    </Button>
                </Space>
            </div>
            {activePreset === 'custom' && (
                <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>
                    Niestandardowe filtry
                </Tag>
            )}
        </div>
    );

    const filterElements = (
        <Space wrap={!isMobile} direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
            <Input.Search
                placeholder="Szukaj (imię, email, nr zamówienia)"
                allowClear
                style={{ width: isMobile ? '100%' : 230 }}
                onSearch={(value) => {
                    setSearch(value || undefined);
                    setPage(1);
                }}
                onChange={(e) => {
                    if (e.target.value === '' && search) {
                        setSearch(undefined);
                        setPage(1);
                    }
                }}
                enterButton
            />
            <Select
                placeholder="Płatność"
                allowClear
                style={{ width: isMobile ? '100%' : 155 }}
                value={billingStatus}
                onChange={(value) => {
                    setBillingStatus(value);
                    setPage(1);
                }}
                options={[
                    { value: 'PAID', label: '✓ Opłacone', disabled: isBillingStatusDisabled('PAID') },
                    { value: 'PARTIALLY_PAID', label: '½ Częściowo opłacone', disabled: isBillingStatusDisabled('PARTIALLY_PAID') },
                    { value: 'PENDING', label: '○ Oczekuje na płatność', disabled: isBillingStatusDisabled('PENDING') },
                    { value: 'OVERDUE', label: '⚠ Po terminie', disabled: isBillingStatusDisabled('OVERDUE') },
                    { value: 'FAILED', label: '✕ Błąd płatności', disabled: isBillingStatusDisabled('FAILED') },
                    { value: 'REFUNDED', label: '↩ Zwrot', disabled: isBillingStatusDisabled('REFUNDED') },
                ]}
            />
            <Select
                placeholder="Realizacja"
                allowClear
                style={{ width: isMobile ? '100%' : 155 }}
                value={status}
                onChange={(value) => {
                    setStatus(value);
                    setPage(1);
                }}
                options={[
                    { value: 'PENDING', label: 'Oczekujące', disabled: isStatusDisabled('PENDING') },
                    { value: 'CONFIRMED', label: 'Potwierdzone', disabled: isStatusDisabled('CONFIRMED') },
                    { value: 'PROCESSING', label: 'W realizacji', disabled: isStatusDisabled('PROCESSING') },
                    { value: 'PACKED', label: 'Spakowane', disabled: isStatusDisabled('PACKED') },
                    { value: 'SHIPPED', label: 'Wysłane', disabled: isStatusDisabled('SHIPPED') },
                    { value: 'DELIVERED', label: 'Dostarczone', disabled: isStatusDisabled('DELIVERED') },
                    { value: 'COMPLETED', label: 'Zakończone', disabled: isStatusDisabled('COMPLETED') },
                    { value: 'ON_HOLD', label: '⚠ Wstrzymane', disabled: isStatusDisabled('ON_HOLD') },
                    { value: 'CANCELLED', label: '✕ Anulowane', disabled: isStatusDisabled('CANCELLED') },
                    { value: 'REFUNDED', label: '↩ Zwrot', disabled: isStatusDisabled('REFUNDED') },
                ]}
            />
            <Select
                placeholder="Kanał"
                allowClear
                style={{ width: isMobile ? '100%' : 130 }}
                value={channelId}
                onChange={(value) => {
                    setChannelId(value);
                    setPage(1);
                }}
                options={channelOptions.map((opt: any) => ({
                    ...opt,
                    disabled: isChannelDisabled(opt.value)
                }))}
            />
            <Select
                placeholder="Typ sprzedaży"
                allowClear
                style={{ width: isMobile ? '100%' : 140 }}
                value={salesType}
                onChange={(value) => {
                    setSalesType(value);
                    setPage(1);
                }}
                options={[
                    { value: 'b2b', label: 'B2B', disabled: isSalesTypeDisabled('b2b') },
                    { value: 'detal', label: 'Detal', disabled: isSalesTypeDisabled('detal') },
                    { value: 'komis', label: 'Komis', disabled: isSalesTypeDisabled('komis') },
                ]}
            />
            <DatePicker.RangePicker
                placeholder={['Data od', 'do']}
                style={{ width: isMobile ? '100%' : 220 }}
                format="DD.MM.YYYY"
                value={tableDateRange}
                onChange={(dates) => {
                    setTableDateRange(dates as any);
                    setPage(1);
                }}
                allowClear
            />
            <Select
                placeholder="Produkt"
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: isMobile ? '100%' : 210 }}
                value={productId}
                onChange={(value) => {
                    setProductId(value);
                    setPage(1);
                }}
                options={productOptions.map((opt: any) => ({
                    ...opt,
                    disabled: isProductDisabled(opt.value)
                }))}
            />
            <Select
                placeholder="Zapisane filtry"
                style={{ width: isMobile ? '100%' : 170 }}
                allowClear
                onChange={(filterId) => {
                    if (filterId) {
                        const filter = (savedFiltersData || []).find((f: any) => f.id === filterId);
                        if (filter) handleApplyFilter(filter);
                    }
                }}
                dropdownRender={(menu) => (
                    <>
                        {menu}
                        <Divider style={{ margin: '8px 0' }} />
                        <Button
                            type="text"
                            icon={<SaveOutlined />}
                            onClick={() => setIsSaveFilterModalOpen(true)}
                            style={{ width: '100%' }}
                        >
                            Zapisz aktualny filtr
                        </Button>
                    </>
                )}
            >
                {(savedFiltersData || []).map((filter: any) => (
                    <Select.Option key={filter.id} value={filter.id}>
                        <Space>
                            <FilterOutlined />
                            {filter.name}
                        </Space>
                    </Select.Option>
                ))}
            </Select>
            {activeFiltersCount > 0 && (
                <Tooltip title="Wyczyść wszystkie filtry">
                    <Button
                        icon={<CloseOutlined />}
                        onClick={clearAllFilters}
                        size="small"
                        style={{ color: token.colorError }}
                    >
                        Wyczyść ({activeFiltersCount})
                    </Button>
                </Tooltip>
            )}
            {!isMobile && (
                <>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => {
                            form.resetFields();
                            setIsModalOpen(true);
                        }}
                    >
                        Dodaj zamówienie
                    </Button>
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={async () => {
                            try {
                                const response = await ordersApi.exportOrders({ status, channelId, search });
                                const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = 'zamowienia.csv';
                                a.click();
                                window.URL.revokeObjectURL(url);
                                message.success('Wyeksportowano zamówienia');
                            } catch (e) {
                                message.error('Błąd eksportu');
                            }
                        }}
                    >
                        Eksport CSV
                    </Button>
                </>
            )}
        </Space>
    );

    return (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {/* Date Range Selector */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                <Select
                    value={datePreset}
                    onChange={(val) => {
                        setDatePreset(val);
                        if (val !== 'custom') setCustomDateRange(null);
                    }}
                    style={{ width: isMobile ? '100%' : 220 }}
                    options={[
                        { value: '7d', label: getPeriodLabel('7d') },
                        { value: '30d', label: getPeriodLabel('30d') },
                        { value: 'mtd', label: getPeriodLabel('mtd') },
                        { value: 'ytd', label: getPeriodLabel('ytd') },
                        { value: 'custom', label: 'Własny zakres' },
                    ]}
                />
                {datePreset === 'custom' && (
                    <DatePicker.RangePicker
                        value={customDateRange}
                        onChange={(dates) => setCustomDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
                        format="DD.MM.YYYY"
                        allowClear={false}
                        style={{ width: isMobile ? '100%' : 'auto' }}
                    />
                )}
            </div>

            {/* KPI Cards - Collapsible section */}
            <Collapse
                activeKey={kpisExpanded ? ['kpis'] : []}
                onChange={(keys) => {
                    const expanded = keys.includes('kpis');
                    setKpisExpanded(expanded);
                    localStorage.setItem('orders-kpis-expanded', String(expanded));
                }}
                items={[{
                    key: 'kpis',
                    label: (
                        <span>
                            <BarChartOutlined style={{ marginRight: 8 }} />
                            Statystyki zamówień
                        </span>
                    ),
                    children: (
                        <Row gutter={16}>
                            <Col xs={12} sm={6}>
                                <Card size="small">
                                    <Tooltip title={
                                        <div>
                                            <strong>Liczba opłaconych zamówień</strong><br />
                                            Tylko zamówienia z paymentStatus = PAID<br /><br />
                                            <strong>Wyklucza:</strong> oczekujące na płatność,<br />
                                            anulowane, zwrócone w całości.
                                        </div>
                                    }>
                                        <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>
                                            Zamówienia <QuestionCircleOutlined style={{ fontSize: 10 }} />
                                        </div>
                                    </Tooltip>
                                    <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 600 }}>
                                        <ShoppingCartOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                                        {totalOrders}
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card size="small">
                                    <Tooltip title={
                                        <div>
                                            Suma opłaconych zamówień minus zwroty.<br />
                                            <strong>Wyklucza:</strong> oczekujące, anulowane, nieopłacone.
                                        </div>
                                    }>
                                        <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>
                                            Przychód brutto <QuestionCircleOutlined style={{ fontSize: 10 }} />
                                        </div>
                                    </Tooltip>
                                    <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 600, color: '#1890ff' }}>
                                        {totalRevenue.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} <span style={{ fontSize: 16 }}>PLN</span>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card size="small">
                                    <Tooltip title={
                                        <div>
                                            <strong>Zysk netto</strong> = Przychód brutto<br />
                                            − Koszt produktów (COGS)<br />
                                            − Prowizja Allegro (SUC)<br />
                                            − Opłaty za promowanie (Ads)<br />
                                            − One Fulfillment<br />
                                            − Smart<br />
                                            − Allegro Pay<br />
                                            − Inne opłaty<br /><br />
                                            Tylko opłacone zamówienia.
                                        </div>
                                    }>
                                        <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>
                                            Zysk netto <QuestionCircleOutlined style={{ fontSize: 10 }} />
                                        </div>
                                    </Tooltip>
                                    <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 600, color: totalProfit >= 0 ? '#52c41a' : '#ff4d4f' }}>
                                        {totalProfit.toLocaleString('pl-PL', { minimumFractionDigits: 2 })} <span style={{ fontSize: 16 }}>PLN</span>
                                    </div>
                                </Card>
                            </Col>
                            <Col xs={12} sm={6}>
                                <Card size="small">
                                    <Tooltip title="Marża % = (Zysk netto / Przychód brutto) × 100. Liczona tylko dla opłaconych zamówień.">
                                        <div style={{ color: '#666', fontSize: 12, marginBottom: 4 }}>
                                            Marża % <QuestionCircleOutlined style={{ fontSize: 10 }} />
                                        </div>
                                    </Tooltip>
                                    {(() => {
                                        const margin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
                                        const color = margin >= 20 ? '#52c41a' : margin >= 10 ? '#faad14' : '#ff4d4f';
                                        return (
                                            <div style={{ fontSize: isMobile ? 20 : 28, fontWeight: 600, color }}>
                                                {margin.toFixed(1)}%
                                            </div>
                                        );
                                    })()}
                                </Card>
                            </Col>
                        </Row>
                    ),
                }]}
            />

            {/* Collapsible Charts Section */}
            <Collapse
                activeKey={chartsExpanded ? ['charts'] : []}
                onChange={(keys) => {
                    const expanded = keys.includes('charts');
                    setChartsExpanded(expanded);
                    localStorage.setItem('orders-charts-expanded', String(expanded));
                }}
                items={[{
                    key: 'charts',
                    label: (
                        <span>
                            <LineChartOutlined style={{ marginRight: 8 }} />
                            Analiza sprzedaży
                        </span>
                    ),
                    children: chartsExpanded ? (
                        <Row gutter={[16, 16]}>
                            {/* Chart A: Product Breakdown - TOP LEFT */}
                            <Col xs={24} lg={16}>
                                <Card size="small" title="Udział produktów w przychodzie (Top 10)">
                                    {productBreakdownData && productBreakdownData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={280}>
                                            <BarChart data={productBreakdownData} layout="vertical" margin={{ left: isMobile ? 60 : 100, right: 20 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis type="number" fontSize={isMobile ? 9 : 11} tickFormatter={formatValue} />
                                                <YAxis type="category" dataKey="name" fontSize={isMobile ? 8 : 10} width={isMobile ? 55 : 95} tick={{ fill: '#333' }} />
                                                <RechartsTooltip
                                                    formatter={(value: number) => [`${(Number(value) || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} PLN`, 'Przychód']}
                                                />
                                                <Bar dataKey="value" fill="#1890ff" name="Przychód" radius={[0, 4, 4, 0]}>
                                                    {productBreakdownData.map((_: any, index: number) => (
                                                        <Cell key={`cell-${index}`} fill={['#1890ff', '#52c41a', '#722ed1', '#faad14', '#13c2c2', '#eb2f96', '#fa8c16', '#2f54eb', '#a0d911', '#ff4d4f'][index % 10]} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: isMobile ? 20 : 40, color: '#999' }}>
                                            <BarChartOutlined style={{ fontSize: 32, marginBottom: 8 }} />
                                            <div>Brak danych o produktach</div>
                                        </div>
                                    )}
                                </Card>
                            </Col>

                            {/* Chart B: Trend Indicator - TOP RIGHT */}
                            <Col xs={24} sm={12} lg={8}>
                                <Card size="small" title="Trend sprzedaży" style={{ height: '100%' }}
                                    styles={{ body: { display: 'flex', flexDirection: 'column', justifyContent: 'center', height: 'calc(100% - 48px)' } }}>
                                    {trendData ? (
                                        <div style={{ textAlign: 'center' }}>
                                            <div style={{
                                                fontSize: isMobile ? 48 : 64,
                                                lineHeight: 1,
                                                marginBottom: 8,
                                                color: trendData.change > 5 ? '#52c41a' : trendData.change < -5 ? '#ff4d4f' : '#faad14'
                                            }}>
                                                {trendData.change > 5 ? '↑' : trendData.change < -5 ? '↓' : '→'}
                                            </div>
                                            <div style={{ fontSize: isMobile ? 24 : 32, fontWeight: 700, color: trendData.change > 0 ? '#52c41a' : trendData.change < 0 ? '#ff4d4f' : '#666' }}>
                                                {trendData.change > 0 ? '+' : ''}{trendData.change.toFixed(1)}%
                                            </div>
                                            <div style={{ color: '#999', fontSize: 12, marginTop: 4, marginBottom: 16 }}>
                                                vs poprzedni okres
                                            </div>
                                            <Row gutter={8} style={{ background: token.colorFillAlter, borderRadius: 8, padding: '12px 8px', margin: '0 -8px' }}>
                                                <Col span={12} style={{ textAlign: 'center', borderRight: `1px solid ${token.colorBorderSecondary}` }}>
                                                    <Tooltip title="Średni dzienny przychód z wybranego okresu">
                                                        <div style={{ fontSize: 11, color: token.colorTextDescription, cursor: 'help' }}>Obecny okres</div>
                                                    </Tooltip>
                                                    <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 600 }}>{(Number(trendData.avgCurrent) || 0).toLocaleString('pl-PL', { maximumFractionDigits: 0 })} zł</div>
                                                </Col>
                                                <Col span={12} style={{ textAlign: 'center' }}>
                                                    <Tooltip title="Średni dzienny przychód z poprzedniego równoważnego okresu">
                                                        <div style={{ fontSize: 11, color: token.colorTextDescription, cursor: 'help' }}>Poprz. okres</div>
                                                    </Tooltip>
                                                    <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 600 }}>{(Number(trendData.avgPrev) || 0).toLocaleString('pl-PL', { maximumFractionDigits: 0 })} zł</div>
                                                </Col>
                                            </Row>
                                        </div>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: isMobile ? 20 : 40, color: '#999' }}>Brak danych</div>
                                    )}
                                </Card>
                            </Col>

                            {/* Chart C: Daily Sales Line Chart - BOTTOM FULL WIDTH */}
                            <Col xs={24}>
                                <Card size="small" title="Sprzedaż dzienna">
                                    {dailySalesData && dailySalesData.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={200}>
                                            <LineChart data={dailySalesData.map((d: any) => ({
                                                ...d,
                                                date: dayjs(d.date).format('DD.MM'),
                                            }))} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="date" fontSize={isMobile ? 9 : 11} />
                                                <YAxis fontSize={isMobile ? 9 : 11} tickFormatter={formatValue} />
                                                <RechartsTooltip
                                                    formatter={(value: number) => [`${(Number(value) || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2 })} PLN`, 'Przychód']}
                                                />
                                                <Line type="monotone" dataKey="revenue" stroke="#1890ff" name="Przychód" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: isMobile ? 20 : 40, color: '#999' }}>Brak danych</div>
                                    )}
                                </Card>
                            </Col>
                        </Row>
                    ) : null,
                }]}
            />


            <Card
                title={isMobile ? (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                        <span>Zamówienia</span>
                        <Space>
                            <Button
                                size="small"
                                icon={filtersExpanded ? <UpOutlined /> : <FilterOutlined />}
                                onClick={() => setFiltersExpanded(!filtersExpanded)}
                            >
                                Filtry
                            </Button>
                            <Button
                                size="small"
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    form.resetFields();
                                    setIsModalOpen(true);
                                }}
                            />
                        </Space>
                    </div>
                ) : "Zamówienia"}
                extra={!isMobile && filterElements}
            >
                {isMobile && filtersExpanded && (
                    <div style={{ marginBottom: 16, background: token.colorFillAlter, padding: 12, borderRadius: 8 }}>
                        {filterElements}
                        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                style={{ flex: 1 }}
                                onClick={() => {
                                    form.resetFields();
                                    setIsModalOpen(true);
                                }}
                            >
                                Dodaj
                            </Button>
                            <Button
                                icon={<DownloadOutlined />}
                                style={{ flex: 1 }}
                                onClick={async () => {
                                    try {
                                        const response = await ordersApi.exportOrders({ status, channelId, search });
                                        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
                                        const url = window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = 'zamowienia.csv';
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                        message.success('Wyeksportowano zamówienia');
                                    } catch (e) {
                                        message.error('Błąd eksportu');
                                    }
                                }}
                            >
                                Eksport
                            </Button>
                        </div>
                    </div>
                )}
                {presetElements}
                {isMobile ? (
                    <List
                        dataSource={[...(orders || [])].sort((a: any, b: any) => {
                            const dateA = dayjs(a.orderDate || a.createdAt);
                            const dateB = dayjs(b.orderDate || b.createdAt);
                            return dateB.valueOf() - dateA.valueOf();
                        })}
                        loading={isLoading}
                        pagination={{
                            current: page,
                            pageSize: pageSize,
                            total: ordersData?.total || 0,
                            size: 'small',
                            showTotal: (total, range) => `${range[0]}-${range[1]}/${total}`,
                            onChange: (newPage, newPageSize) => {
                                setPage(newPage);
                                if (newPageSize !== pageSize) {
                                    setPageSize(newPageSize);
                                    setPage(1);
                                }
                            },
                        }}
                        renderItem={(record: any) => {
                            const shortNum = getShortOrderNumber(record);
                            const salesType = getSalesType(record);
                            const billing = getBillingStatus(record);
                            const trackingUrl = record.trackingNumber ? getTrackingUrl(record.trackingNumber, record.shippingMethod) : null;
                            const isCancelled = record.status === 'CANCELLED';
                            const isRefunded = record.status === 'REFUNDED' || record.paymentStatus === 'REFUNDED' || Number(record.refundAmount) > 0;
                            const isZeroProfit = isCancelled || isRefunded;
                            const profit = isZeroProfit ? 0 : (record.netProfit !== null && record.netProfit !== undefined 
                                ? Number(record.netProfit) 
                                : Number(record.grossProfit || 0));
                            const margin = !isZeroProfit && record.total > 0 ? (profit / Number(record.total)) * 100 : 0;
                            const profitability = getOrderProfitability(record);
                            const customerName = record.customerName ||
                                (record.customer
                                    ? `${record.customer.firstName || ''} ${record.customer.lastName || ''}`
                                    : record.shippingName || '-');
                            
                            const shippingColors: Record<string, string> = {
                                PENDING: 'gold',
                                CONFIRMED: 'blue',
                                PROCESSING: 'cyan',
                                PACKED: 'purple',
                                SHIPPED: 'geekblue',
                                DELIVERED: 'green',
                                COMPLETED: 'green',
                                CANCELLED: 'red',
                                REFUNDED: 'red',
                                ON_HOLD: 'orange',
                            };
                            
                            return (
                                <List.Item style={{ padding: '8px 0', border: 'none' }}>
                                    <Card
                                        size="small"
                                        hoverable
                                        style={{ width: '100%', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}
                                        onClick={() => {
                                            setSelectedOrder(record);
                                            setIsDetailModalOpen(true);
                                        }}
                                        styles={{ body: { padding: '12px' } }}
                                    >
                                        {/* Nagłówek: Identyfikator (lewo) + Wartość (prawo) */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                                            <div style={{ fontWeight: 700, fontSize: 14, color: token.colorPrimary, display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Checkbox
                                                    checked={selectedRowKeys.includes(record.id)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        if (e.target.checked) {
                                                            setSelectedRowKeys([...selectedRowKeys, record.id]);
                                                        } else {
                                                            setSelectedRowKeys(selectedRowKeys.filter(id => id !== record.id));
                                                        }
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ marginRight: 2 }}
                                                />
                                                {shortNum}
                                                {(record.customerNote || record.internalNote) && (
                                                    <MessageOutlined style={{ color: '#faad14', fontSize: 12 }} />
                                                )}
                                            </div>
                                            <span style={{ fontWeight: 700, fontSize: 14 }}>
                                                {new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(record.total || 0)}
                                            </span>
                                        </div>

                                        {/* Klient — główna linia */}
                                        <div style={{ marginBottom: 6 }}>
                                            <div style={{ fontSize: 13, fontWeight: 500, color: token.colorText }}>
                                                {customerName}
                                            </div>
                                            {salesType.label === 'B2B' && record.contractorId ? (
                                                <Tooltip title="Otwórz kartę firmy w CRM">
                                                    <a
                                                        href={`/b2b/${record.contractorId}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{
                                                            fontSize: 11,
                                                            color: token.colorPrimary,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 4,
                                                            marginTop: 2,
                                                        }}
                                                    >
                                                        <BankOutlined style={{ fontSize: 11 }} />
                                                        Karta firmy
                                                    </a>
                                                </Tooltip>
                                            ) : (
                                                <span style={{ fontSize: 11, color: token.colorTextDescription }}>
                                                    {salesType.label}
                                                </span>
                                            )}
                                        </div>

                                        {/* Produkty w zamówieniu */}
                                        <OrderProductsCell lines={record.lines} maxVisible={3} variant="card" />

                                        {/* Statusy: Płatność + Realizacja */}
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                                            <Tag
                                                color={billing.color}
                                                style={{ margin: 0, fontSize: 10, fontWeight: billing.problem ? 700 : 400 }}
                                            >
                                                {billing.label}
                                            </Tag>
                                            <Tag
                                                color={shippingColors[record.status] || 'default'}
                                                style={{ margin: 0, fontSize: 10, fontWeight: ['CANCELLED','REFUNDED','ON_HOLD'].includes(record.status) ? 700 : 400 }}
                                            >
                                                {statusLabels[record.status] || record.status}
                                            </Tag>
                                            {trackingUrl && (
                                                <a
                                                    href={trackingUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{ fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 2, color: token.colorPrimary }}
                                                >
                                                    Śledź <LinkOutlined style={{ fontSize: 9 }} />
                                                </a>
                                            )}
                                        </div>

                                        {/* Metadane: data + kanał + marża */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 6 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{ fontSize: 11, color: token.colorTextDescription }}>
                                                    {dayjs(record.orderDate || record.createdAt).format('DD.MM.YY HH:mm')}
                                                </span>
                                                <span style={{ fontSize: 11, color: token.colorTextDescription }}>·</span>
                                                <span style={{ fontSize: 11, color: token.colorTextDescription }}>
                                                    {record.channel?.name || 'Ręczne'}
                                                </span>
                                            </div>
                                            {(profit !== 0 || isZeroProfit) && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                    {(() => {
                                                        const isUnreliable = ['INCOMPLETE_COGS', 'ESTIMATED_ALLEGRO', 'UNPAID', 'PARTIALLY_PAID'].includes(profitability.status);
                                                        let mColor = '#52c41a'; // green
                                                        if (isZeroProfit) {
                                                            mColor = token.colorTextDescription; // grey
                                                        } else if (profitability.status === 'UNPAID') {
                                                            mColor = '#1890ff'; // potential blue
                                                        } else if (profitability.status === 'PARTIALLY_PAID') {
                                                            mColor = '#722ed1'; // purple
                                                        } else if (profitability.status === 'INCOMPLETE_COGS' || profitability.status === 'ESTIMATED_ALLEGRO') {
                                                            mColor = '#d46b08'; // warning orange
                                                        } else if (profit < 0) {
                                                            mColor = '#ff4d4f'; // red
                                                        }

                                                        return (
                                                            <span style={{ fontSize: 11, color: mColor, fontWeight: isZeroProfit ? 400 : 600 }}>
                                                                {isUnreliable ? '~ ' : ''}{new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(profit)}
                                                                {!isZeroProfit && <span style={{ color: token.colorTextDescription, fontWeight: 400 }}> ({isUnreliable ? '~ ' : ''}{margin.toFixed(0)}%)</span>}
                                                            </span>
                                                        );
                                                    })()}
                                                    <Badge color={profitability.color} style={{ transform: 'scale(0.8)', marginLeft: 2 }} />
                                                    <span style={{ fontSize: 9, color: profitability.color, fontWeight: 500 }}>
                                                        {profitability.badgeLabel}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </Card>
                                </List.Item>
                            );
                        }}
                    />
                ) : (
                    <Table
                        rowSelection={{
                            selectedRowKeys,
                            onChange: (newSelectedRowKeys: React.Key[]) => {
                                setSelectedRowKeys(newSelectedRowKeys);
                            },
                        }}
                        dataSource={orders}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            current: page,
                            pageSize: pageSize,
                            total: ordersData?.total || 0,
                            showSizeChanger: true,
                            showTotal: (total, range) => isMobile ? `${range[0]}-${range[1]}/${total}` : `${range[0]}-${range[1]} z ${total}`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            onChange: (newPage, newPageSize) => {
                                setPage(newPage);
                                if (newPageSize !== pageSize) {
                                    setPageSize(newPageSize);
                                    setPage(1);
                                }
                            },
                        }}
                        onChange={(pagination, filters, sorter: any) => {
                            if (sorter.field && sorter.order) {
                                const fieldMap: Record<string, string> = {
                                    'orderDate': 'orderDate',
                                    'orderNumber': 'orderNumber',
                                    'customerName': 'customerName',
                                    'subtotal': 'subtotal',
                                    'status': 'status',
                                    'createdAt': 'createdAt',
                                };
                                setSortField(fieldMap[sorter.field] || 'createdAt');
                                setSortOrder(sorter.order === 'ascend' ? 'ASC' : 'DESC');
                            } else {
                                setSortField(undefined);
                                setSortOrder(undefined);
                            }
                        }}
                        scroll={{ x: isMobile ? 1000 : 1500 }}
                        onRow={(record) => ({
                            onClick: () => {
                                setSelectedOrder(record);
                                setIsDetailModalOpen(true);
                            },
                            style: { cursor: 'pointer' },
                        })}
                    />
                )}
            </Card>

            <Modal
                title="Nowe zamówienie"
                open={isModalOpen}
                onCancel={() => {
                    setIsModalOpen(false);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                confirmLoading={createOrderMutation.isPending}
                width={isMobile ? '100%' : 700}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="channelId" label="Kanał sprzedaży">
                                <Select
                                    options={[
                                        ...channelOptions,
                                        { label: '+ Dodaj nowy', value: 'new_channel_trigger' }
                                    ]}
                                    placeholder="Wybierz kanał (opcjonalnie)"
                                    allowClear
                                    onChange={async (val: any) => {
                                        if (val === 'new_channel_trigger') {
                                            const name = prompt('Nazwa nowego kanału:');
                                            if (name) {
                                                try {
                                                    const res = await channelsApi.create({ name });
                                                    queryClient.invalidateQueries({ queryKey: ['channels'] });
                                                    form.setFieldsValue({ channelId: res.data.id });
                                                    message.success('Kanał utworzony');
                                                } catch (error) {
                                                    console.error(error);
                                                    message.error('Błąd tworzenia kanału');
                                                }
                                            }
                                        } else {
                                            form.setFieldsValue({ channelId: val });
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="orderNumber" label="Numer zamówienia">
                                <Input placeholder="np. ZAM-2026-001 (auto jeśli puste)" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="contractorId" label="Kontrahent B2B" help="Wybierz kontrahenta, aby automatycznie uzupełnić dane.">
                                <ContractorSelect
                                    onChange={handleContractorSelect}
                                    value={selectedContractor?.id}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name={['customFields', 'salesType']} label="Typ sprzedaży" initialValue="detal">
                                <Select
                                    options={[
                                        { label: 'Detal', value: 'detal' },
                                        { label: 'B2B', value: 'b2b' },
                                        { label: 'Komis', value: 'komis' },
                                    ]}
                                    placeholder="Wybierz typ sprzedaży"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    {selectedContractor && (
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={24}>
                                <ContractorMiniInfo contractor={selectedContractor} />
                            </Col>
                        </Row>
                    )}

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="locationId"
                                label="Magazyn wysyłki"
                                rules={[{ required: true, message: 'Wybierz magazyn' }]}
                            >
                                <Select
                                    options={locationOptions}
                                    placeholder="Z którego magazynu wysłać?"
                                    onChange={(value) => {
                                        setSelectedLocationId(value);
                                        // Clear product selections when location changes
                                        const lines = form.getFieldValue('lines') || [];
                                        if (lines.length > 0) {
                                            form.setFieldsValue({
                                                lines: lines.map((l: any) => ({ ...l, productId: undefined }))
                                            });
                                        }
                                    }}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="orderDate"
                                label="Data zamówienia"
                                tooltip="Pozostaw puste dla bieżącej daty"
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                    placeholder="Domyślnie: dzisiaj"
                                    showTime={false}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>Dane klienta</Divider>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="shippingName"
                                label="Imię i nazwisko / firma"
                                rules={[{ required: true, message: 'Podaj dane odbiorcy' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="shippingPhone"
                                label="Telefon"
                                rules={[
                                    {
                                        pattern: /^(\+48)?[\s-]?\d{3}[\s-]?\d{3}[\s-]?\d{3}$/,
                                        message: 'Nieprawidłowy format telefonu (np. 123456789 lub +48 123 456 789)',
                                    },
                                ]}
                            >
                                <Input placeholder="+48 123 456 789" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={16}>
                            <Form.Item name="shippingStreet" label="Ulica i numer">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="shippingCity" label="Miasto">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item
                                name="shippingPostalCode"
                                label="Kod pocztowy"
                                rules={[
                                    {
                                        pattern: /^\d{2}-\d{3}$/,
                                        message: 'Nieprawidłowy format kodu (np. 00-123)',
                                    },
                                ]}
                            >
                                <Input placeholder="00-123" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>Wysyłka i płatność (opcjonalne)</Divider>
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item name="shippingMethod" label="Metoda wysyłki">
                                <Input placeholder="np. Kurier DPD" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="paymentMethod" label="Metoda płatności">
                                <Input placeholder="np. Przelew, PayPal" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={8}>
                            <Form.Item name="paymentStatus" label="Status płatności" initialValue="PENDING">
                                <Select
                                    options={[
                                        { value: 'PENDING', label: 'Oczekuje' },
                                        { value: 'PAID', label: 'Zapłacone' },
                                        { value: 'PARTIALLY_PAID', label: 'Częściowe' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev?.paymentStatus !== cur?.paymentStatus}>
                        {({ getFieldValue }) => getFieldValue('paymentStatus') === 'PARTIALLY_PAID' && (
                            <Row gutter={16}>
                                <Col xs={24} sm={8}>
                                    <Form.Item name="paidAmount" label="Wpłacona kwota" rules={[{ required: true, message: 'Kwota jest wymagana' }]}>
                                        <InputNumber style={{ width: '100%' }} min={0} precision={2} addonAfter="PLN" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                    </Form.Item>

                    <Divider>Notatki (opcjonalne)</Divider>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="customerNote" label="Notatki dla klienta">
                                <Input.TextArea rows={2} placeholder="Widoczne dla klienta" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="internalNote" label="Notatki wewnętrzne">
                                <Input.TextArea rows={2} placeholder="Tylko do użytku wewnętrznego" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>Pozycje zamówienia</Divider>

                    <Form.List name="items">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Row gutter={16} key={key} align="middle">
                                        <Col xs={24} sm={10}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'productId']}
                                                rules={[{ required: true, message: 'Wybierz produkt' }]}
                                            >
                                                <Select
                                                    options={productOptions}
                                                    placeholder="Produkt"
                                                    showSearch
                                                    optionFilterProp="label"
                                                    onChange={(value) => {
                                                        const product = productOptions.find(
                                                            (p: any) => p.value === value
                                                        );
                                                        if (product) {
                                                            const items = form.getFieldValue('items');
                                                            items[name].unitPrice = product.price;
                                                            form.setFieldsValue({ items });
                                                        }
                                                    }}
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={12} sm={5}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'quantity']}
                                                rules={[{ required: true }]}
                                            >
                                                <InputNumber placeholder="Ilość" min={1} style={{ width: '100%' }} />
                                            </Form.Item>
                                            <Form.Item noStyle shouldUpdate={(prevValues, curValues) => {
                                                const prevProduct = prevValues?.items?.[name]?.productId;
                                                const curProduct = curValues?.items?.[name]?.productId;
                                                return prevProduct !== curProduct;
                                            }}>
                                                {({ getFieldValue }) => {
                                                    const productId = getFieldValue(['items', name, 'productId']);
                                                    if (!productId) return null;
                                                    const stock = getStockForProduct(productId);
                                                    return (
                                                        <div style={{ fontSize: '11px', color: stock > 0 ? '#52c41a' : '#ff4d4f', marginTop: '-20px' }}>
                                                            Dostępne: <strong>{stock}</strong> szt.
                                                        </div>
                                                    );
                                                }}
                                            </Form.Item>
                                        </Col>
                                        <Col xs={12} sm={6}>
                                            <Form.Item
                                                {...restField}
                                                name={[name, 'unitPrice']}
                                                rules={[{ required: true }]}
                                            >
                                                <InputNumber
                                                    placeholder="Cena"
                                                    min={0}
                                                    precision={2}
                                                    style={{ width: '100%' }}
                                                    addonAfter="PLN"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={3}>
                                            <Button
                                                type={isMobile ? "primary" : "text"}
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(name)}
                                                style={{ width: isMobile ? '100%' : 'auto', marginBottom: isMobile ? 16 : 0 }}
                                            >
                                                {isMobile ? 'Usuń pozycję' : ''}
                                            </Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                                    Dodaj pozycję
                                </Button>
                            </>
                        )}
                    </Form.List>

                    {/* Order Summary Preview */}
                    <Form.Item noStyle shouldUpdate>
                        {() => {
                            const items = form.getFieldValue('items') || [];
                            const shippingAmount = form.getFieldValue('shippingAmount') || 0;
                            const subtotal = items.reduce((sum: number, item: any) => {
                                return sum + (Number(item?.quantity) || 0) * (Number(item?.unitPrice) || 0);
                            }, 0);
                            const total = subtotal + Number(shippingAmount);

                            return items.length > 0 ? (
                                <Card size="small" style={{ marginTop: 16, marginBottom: 16, background: token.colorSuccessBg }}>
                                    <Row gutter={16}>
                                        <Col xs={24} sm={8}>
                                            <Statistic title="Pozycji" value={items.length} />
                                        </Col>
                                        <Col xs={24} sm={8}>
                                            <Statistic title="Produkty" value={subtotal} precision={2} suffix="PLN" />
                                        </Col>
                                        <Col xs={24} sm={8}>
                                            <Statistic title="Razem" value={total} precision={2} suffix="PLN" valueStyle={{ color: '#3f8600' }} />
                                        </Col>
                                    </Row>
                                </Card>
                            ) : null;
                        }}
                    </Form.Item>

                    <Divider />

                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item name="shippingAmount" label="Koszt wysyłki" initialValue={0}>
                                <InputNumber style={{ width: '100%' }} min={0} precision={2} addonAfter="PLN" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* Order Detail Modal */}
            <Modal
                title={`Zamówienie ${selectedOrder ? getShortOrderNumber(selectedOrder) : ''}`}
                open={isDetailModalOpen}
                onCancel={() => {
                    setIsDetailModalOpen(false);
                    setSelectedOrder(null);
                }}
                footer={null}
                width={isMobile ? '100%' : 1200}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
                styles={{ body: { padding: isMobile ? '8px' : '16px' } }}
            >
                {selectedOrder && (() => {
                    const shortNum = getShortOrderNumber(selectedOrder);
                    const sType = getSalesType(selectedOrder);
                    const bStatus = getBillingStatus(selectedOrder);

                    // Calculations
                    const lines = groupOrderLines(selectedOrder.lines || []);
                    const calculatedTotal = lines.reduce((sum: number, line: any) =>
                        sum + (Number(line.quantity) * Number(line.unitPrice || 0)), 0);
                    const calculatedCost = lines.reduce((sum: number, line: any) =>
                        sum + (Number(line.quantity) * Number(line.unitCost || 0)), 0);

                    const totalAmount = Number(selectedOrder.total) || calculatedTotal || 0;
                    const shippingAmount = Number(selectedOrder.shippingAmount) || 0;
                    const commission = Number(selectedOrder.channelCommission) || 0;
                    const otherFees = Number(selectedOrder.channelOtherFees) || 0;
                    const totalCost = Number(selectedOrder.totalCost) || calculatedCost || 0;

                    const dbGrossProfit = selectedOrder.grossProfit !== null && selectedOrder.grossProfit !== undefined ? Number(selectedOrder.grossProfit) : null;
                    const dbNetProfit = selectedOrder.netProfit !== null && selectedOrder.netProfit !== undefined ? Number(selectedOrder.netProfit) : null;

                    const grossProfit = dbGrossProfit !== null ? dbGrossProfit : (totalAmount - totalCost - commission - otherFees);
                    const profit = dbNetProfit !== null ? dbNetProfit : grossProfit;
                    const margin = totalAmount > 0 ? (profit / totalAmount) * 100 : 0;

                    const isAllegroOrder = selectedOrder.channel?.name?.toLowerCase().includes('allegro') || !!selectedOrder.externalId;
                    const rawPaidAmount = Number(selectedOrder.paidAmount || 0);
                    const paidAmount = (isAllegroOrder && selectedOrder.paymentStatus === 'PAID' && rawPaidAmount === 0)
                        ? totalAmount
                        : rawPaidAmount;
                    const remainingAmount = Math.max(0, totalAmount - paidAmount);

                    const orderDateObj = dayjs(selectedOrder.orderDate || selectedOrder.createdAt);
                    const isOverdue = selectedOrder.paymentStatus !== 'PAID' && orderDateObj.isBefore(dayjs().subtract(7, 'days'));

                    const hasPaymentWarning = selectedOrder.paymentStatus === 'FAILED' || isOverdue;
                    const hasFulfillmentWarning = selectedOrder.status === 'ON_HOLD' || selectedOrder.status === 'CANCELLED';

                    const fulfillmentColors: Record<string, string> = {
                        PENDING: 'gold',
                        CONFIRMED: 'cyan',
                        PROCESSING: 'blue',
                        PACKED: 'orange',
                        SHIPPED: 'blue',
                        DELIVERED: 'green',
                        COMPLETED: 'green',
                        CANCELLED: 'red',
                        REFUNDED: 'magenta',
                        ON_HOLD: 'volcano',
                    };

                    const displayLines = [...lines];
                    if (shippingAmount > 0) {
                        displayLines.push({
                            id: 'shipping-cost',
                            name: 'Koszty wysyłki',
                            sku: 'WYSYŁKA',
                            quantity: 1,
                            unitPrice: shippingAmount,
                            total: shippingAmount,
                            profit: 0,
                            isShipping: true,
                        });
                    }

                    return (
                        <Space direction="vertical" size={isMobile ? "small" : "middle"} style={{ width: '100%' }}>
                            {/* Alert for refunded orders */}
                            {(selectedOrder.status === 'REFUNDED' || selectedOrder.paymentStatus === 'REFUNDED') && (
                                <Card size="small" style={{ background: token.colorErrorBg, border: `1px solid ${token.colorErrorBorder}` }}>
                                    <strong style={{ color: token.colorError }}>⚠️ ZAMÓWIENIE ZWRÓCONE</strong>
                                    {selectedOrder.refundAmount > 0 && (
                                        <span> - Zwrócono: <strong>{Number(selectedOrder.refundAmount).toFixed(2)} PLN</strong></span>
                                    )}
                                    {selectedOrder.goodsReturned && (
                                        <span> | Towar zwrócony: <Tag color="green">TAK</Tag></span>
                                    )}
                                    {!selectedOrder.goodsReturned && (
                                        <span> | Towar zwrócony: <Tag color="orange">NIE</Tag></span>
                                    )}
                                </Card>
                            )}

                            <Row gutter={[24, 24]}>
                                {/* Left main column */}
                                <Col xs={24} lg={16}>
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        {/* 1. Top Mixed Summary */}
                                        <div style={{
                                            background: token.colorFillAlter,
                                            border: `1px solid ${token.colorBorderSecondary}`,
                                            borderRadius: '8px',
                                            padding: isMobile ? '12px' : '16px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 12
                                        }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: 18, fontWeight: 'bold', color: token.colorPrimary }}>
                                                        {shortNum}
                                                    </span>
                                                    <Tag color={sType.color} style={{ margin: 0 }}>{sType.label}</Tag>
                                                    <Tag style={{ margin: 0 }}>{selectedOrder.channel?.name || 'Ręczne'}</Tag>
                                                    <span style={{ fontSize: 12, color: token.colorTextDescription }}>
                                                        {dayjs(selectedOrder.orderDate || selectedOrder.createdAt).format('DD.MM.YYYY HH:mm')}
                                                    </span>
                                                </div>
                                                
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                    <Tag color={isOverdue ? 'red' : (selectedOrder.paymentStatus === 'PAID' ? 'green' : 'gold')} style={{ fontWeight: 600, margin: 0 }}>
                                                        {isOverdue ? 'PO TERMINIE ⚠️' : (bStatus.label)}
                                                    </Tag>
                                                    <Tag color={fulfillmentColors[selectedOrder.status] || 'default'} style={{ fontWeight: 600, margin: 0 }}>
                                                        {statusLabels[selectedOrder.status] || selectedOrder.status}
                                                    </Tag>
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, borderTop: `1px solid ${token.colorBorderSecondary}`, paddingTop: 10 }}>
                                                <div>
                                                    <span style={{ fontSize: 12, color: token.colorTextDescription, display: 'block' }}>Klient / Firma</span>
                                                    <span style={{ fontSize: 14, fontWeight: '500', color: token.colorText }}>
                                                        {selectedOrder.customerName || selectedOrder.shippingName || '-'}
                                                    </span>
                                                    {sType.label === 'B2B' && selectedOrder.contractorId && (
                                                        <a 
                                                            href={`/b2b/${selectedOrder.contractorId}`} 
                                                            onClick={(e) => e.stopPropagation()} 
                                                            style={{ fontSize: 12, fontWeight: '600', marginLeft: 8 }}
                                                        >
                                                            Karta CRM 🏢
                                                        </a>
                                                    )}
                                                </div>

                                                <div style={{ textAlign: 'right' }}>
                                                    <span style={{ fontSize: 12, color: token.colorTextDescription, display: 'block' }}>Wartość (Brutto)</span>
                                                    <span style={{ fontSize: 16, fontWeight: 'bold', color: token.colorText }}>
                                                        {totalAmount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}
                                                    </span>
                                                    {selectedOrder.paymentStatus !== 'PAID' && (
                                                        <span style={{ fontSize: 11, color: token.colorError, display: 'block', fontWeight: '500' }}>
                                                            Pozostało: {remainingAmount.toFixed(2)} PLN
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {(hasPaymentWarning || hasFulfillmentWarning) && (
                                                <div style={{
                                                    padding: '8px 12px',
                                                    background: token.colorWarningBg,
                                                    border: `1px solid ${token.colorWarningBorder}`,
                                                    borderRadius: '4px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    fontSize: 12,
                                                    color: token.colorWarningText
                                                }}>
                                                    <InfoCircleOutlined />
                                                    <span>
                                                        <strong>Problem:</strong>{' '}
                                                        {isOverdue && 'Płatność jest przeterminowana (powyżej 7 dni). '}
                                                        {selectedOrder.paymentStatus === 'FAILED' && 'Ostatnia płatność nie powiodła się. '}
                                                        {selectedOrder.status === 'ON_HOLD' && 'Zamówienie wstrzymane. '}
                                                        {selectedOrder.status === 'CANCELLED' && 'Zamówienie zostało anulowane. '}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* 2. Finanse */}
                                        {(() => {
                                             const isAllegroOrder = selectedOrder.channel?.name?.toLowerCase().includes('allegro') || !!selectedOrder.externalId;
                                             const isCancelled = selectedOrder.status === 'CANCELLED';
                                             const isRefunded = selectedOrder.status === 'REFUNDED' || selectedOrder.paymentStatus === 'REFUNDED' || Number(selectedOrder.refundAmount) > 0;
                                             const isZeroProfit = isCancelled || isRefunded;

                                             // Allegro fees (enforce absolute values for sign consistency)
                                             const allegroCommission = Math.abs(Number(selectedOrder.allegroCommission || 0));
                                             const allegroPromotion = Math.abs(Number(selectedOrder.allegroPromotion || 0));
                                             const allegroFulfillment = Math.abs(Number(selectedOrder.allegroFulfillment || 0));
                                             const allegroSmart = Math.abs(Number(selectedOrder.allegroSmart || 0));
                                             const allegroPayment = Math.abs(Number(selectedOrder.allegroPayment || 0));
                                             const allegroOther = Math.abs(Number(selectedOrder.allegroOther || 0));
                                             const totalAllegroFees = allegroCommission + allegroPromotion + allegroFulfillment + allegroSmart + allegroPayment + allegroOther;

                                             // General fees
                                             const channelCommission = Math.abs(Number(selectedOrder.channelCommission || 0));
                                             const channelShippingCost = Math.abs(Number(selectedOrder.channelShippingCost || 0));
                                             const channelOtherFees = Math.abs(Number(selectedOrder.channelOtherFees || 0));

                                             const totalDirectFees = isZeroProfit ? 0 : (isAllegroOrder 
                                                 ? totalAllegroFees 
                                                 : (channelCommission + channelShippingCost + channelOtherFees));

                                             const displayTotalCost = isZeroProfit ? 0 : totalCost;

                                             // Recalculating profit on frontend using absolute cost values
                                             const grossProfit = isZeroProfit ? 0 : (dbGrossProfit !== null ? dbGrossProfit : (totalAmount - totalCost));
                                             const profit = isZeroProfit ? 0 : (dbNetProfit !== null ? dbNetProfit : (grossProfit - totalDirectFees));
                                             const margin = !isZeroProfit && totalAmount > 0 ? (profit / totalAmount) * 100 : 0;

                                             const profitability = getOrderProfitability(selectedOrder);
                                             const isUnreliable = ['INCOMPLETE_COGS', 'ESTIMATED_ALLEGRO', 'UNPAID', 'PARTIALLY_PAID'].includes(profitability.status);
                                             const totalCostsSum = displayTotalCost + totalDirectFees;

                                             return (
                                                 <Card size="small" title={<span>📊 Wynik Finansowy i Koszty</span>}>
                                                     <Row gutter={[16, 16]}>
                                                         <Col xs={24} md={12}>
                                                             <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${token.colorBorderSecondary}`, paddingBottom: 4 }}>
                                                                     <span style={{ color: token.colorTextSecondary, fontSize: 13 }}>
                                                                         Przychód (Brutto):
                                                                         <Tooltip title="Całkowita wartość zamówienia opłacona przez klienta (wraz z dostawą).">
                                                                             <InfoCircleOutlined style={{ color: '#999', fontSize: 10, marginLeft: 4 }} />
                                                                         </Tooltip>
                                                                     </span>
                                                                     <span style={{ fontWeight: 'bold', color: isZeroProfit ? token.colorTextDescription : token.colorText }}>
                                                                         {isZeroProfit 
                                                                             ? `0,00 PLN (${isCancelled ? 'Anulowane' : 'Zwrot'}, pierwotnie: ${totalAmount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })})`
                                                                             : totalAmount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })
                                                                         }
                                                                     </span>
                                                                 </div>
                                                                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${token.colorBorderSecondary}`, paddingBottom: 4 }}>
                                                                     <span style={{ color: token.colorTextSecondary, fontSize: 13 }}>
                                                                         Wynik na zamówieniu:
                                                                         <Tooltip title="Wynik po odliczeniu kosztu produktów (COGS) oraz bezpośrednich opłat transakcyjnych i wysyłkowych. Nie uwzględnia ogólnych kosztów działalności ani reklam rozliczanych kontowo.">
                                                                             <InfoCircleOutlined style={{ color: '#999', fontSize: 10, marginLeft: 4 }} />
                                                                         </Tooltip>
                                                                     </span>
                                                                     {(() => {
                                                                         let profitColor = '#52c41a'; // green
                                                                         if (isZeroProfit) {
                                                                             profitColor = token.colorTextDescription; // grey
                                                                         } else if (profitability.status === 'UNPAID') {
                                                                             profitColor = '#1890ff'; // potential blue
                                                                         } else if (profitability.status === 'PARTIALLY_PAID') {
                                                                             profitColor = '#722ed1'; // purple
                                                                         } else if (profitability.status === 'INCOMPLETE_COGS' || profitability.status === 'ESTIMATED_ALLEGRO') {
                                                                             profitColor = '#d46b08'; // warning orange
                                                                         } else if (profit < 0) {
                                                                             profitColor = '#ff4d4f'; // red
                                                                         }
                                                                         
                                                                         return (
                                                                             <span style={{ fontWeight: 'bold', color: profitColor }}>
                                                                                 {isZeroProfit 
                                                                                     ? `0,00 PLN (${isCancelled ? '—' : 'Zwrot'})` 
                                                                                     : `${isUnreliable ? '~ ' : ''}${profit.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })} (${isUnreliable ? '~ ' : ''}${margin.toFixed(1)}%)`
                                                                                 }
                                                                             </span>
                                                                         );
                                                                     })()}
                                                                 </div>
                                                                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${token.colorBorderSecondary}`, paddingBottom: 4 }}>
                                                                     <span style={{ color: token.colorTextSecondary, fontSize: 13 }}>Wiarygodność wyniku:</span>
                                                                     <Tag color={profitability.color} style={{ margin: 0, fontSize: 11, fontWeight: 600 }}>
                                                                         {profitability.label}
                                                                     </Tag>
                                                                 </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${token.colorBorderSecondary}`, paddingBottom: 4 }}>
                                                                    <span style={{ color: token.colorTextSecondary, fontSize: 13 }}>
                                                                        {isAllegroOrder ? 'Zapłacono (Allegro):' : 'Zapłacono:'}
                                                                    </span>
                                                                    <span style={{ color: token.colorText, fontWeight: isAllegroOrder && selectedOrder.paymentStatus === 'PAID' ? 'bold' : 'normal' }}>
                                                                        {paidAmount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}
                                                                    </span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${token.colorBorderSecondary}`, paddingBottom: 4 }}>
                                                                    <span style={{ color: token.colorTextSecondary, fontSize: 13 }}>Pozostało:</span>
                                                                    <span style={{ 
                                                                        fontWeight: 'bold', 
                                                                        color: remainingAmount > 0 
                                                                            ? token.colorError 
                                                                            : (isAllegroOrder && selectedOrder.paymentStatus === 'PAID' ? token.colorSuccess : token.colorTextDescription) 
                                                                    }}>
                                                                        {remainingAmount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' })}
                                                                        {isAllegroOrder && selectedOrder.paymentStatus === 'PAID' && (
                                                                            <span style={{ fontSize: 11, fontWeight: 'normal', marginLeft: 4, color: token.colorSuccess }}>
                                                                                (Rozliczone)
                                                                            </span>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                        
                                                        <Col xs={24} md={12}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, background: token.colorFillAlter, padding: '10px 12px', borderRadius: 6, border: `1px dashed ${token.colorBorderSecondary}` }}>
                                                                <div style={{ fontSize: 12, fontWeight: '600', color: token.colorTextSecondary, marginBottom: 2 }}>Koszty bezpośrednie zamówienia:</div>
                                                                
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: token.colorText }}>
                                                                    <span>Koszt produktów (COGS):</span>
                                                                    <span style={{ color: isZeroProfit ? token.colorTextDescription : token.colorError }}>
                                                                        {isZeroProfit ? '0.00 PLN' : `-${totalCost.toFixed(2)} PLN`}
                                                                    </span>
                                                                </div>

                                                                {isAllegroOrder ? (
                                                                    <>
                                                                        {allegroCommission > 0 && (
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: token.colorText }}>
                                                                                <span>Prowizja Allegro (SUC):</span>
                                                                                <span style={{ color: isZeroProfit ? token.colorTextDescription : token.colorError }}>
                                                                                    {isZeroProfit ? '0.00 PLN' : `-${allegroCommission.toFixed(2)} PLN`}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        {allegroPromotion > 0 && (
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: token.colorText }}>
                                                                                <span>Allegro Ads/Promowanie (NSP):</span>
                                                                                <span style={{ color: isZeroProfit ? token.colorTextDescription : token.colorError }}>
                                                                                    {isZeroProfit ? '0.00 PLN' : `-${allegroPromotion.toFixed(2)} PLN`}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        {(allegroFulfillment > 0 || allegroSmart > 0) && (
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: token.colorText }}>
                                                                                <span>Dostawa Allegro (ONE/Smart):</span>
                                                                                <span style={{ color: isZeroProfit ? token.colorTextDescription : token.colorError }}>
                                                                                    {isZeroProfit ? '0.00 PLN' : `-${(allegroFulfillment + allegroSmart).toFixed(2)} PLN`}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        {allegroPayment > 0 && (
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: token.colorText }}>
                                                                                <span>Obsługa płatności (PAY):</span>
                                                                                <span style={{ color: isZeroProfit ? token.colorTextDescription : token.colorError }}>
                                                                                    {isZeroProfit ? '0.00 PLN' : `-${allegroPayment.toFixed(2)} PLN`}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                        {allegroOther > 0 && (
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: token.colorText }}>
                                                                                <span>Inne opłaty Allegro:</span>
                                                                                <span style={{ color: isZeroProfit ? token.colorTextDescription : token.colorError }}>
                                                                                    {isZeroProfit ? '0.00 PLN' : `-${allegroOther.toFixed(2)} PLN`}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: token.colorText }}>
                                                                            <span>Koszt wysyłki (kurier):</span>
                                                                            <span style={{ color: isZeroProfit ? token.colorTextDescription : token.colorError }}>
                                                                                {isZeroProfit ? '0.00 PLN' : (channelShippingCost > 0 ? `-${channelShippingCost.toFixed(2)} PLN` : '0.00 PLN')}
                                                                            </span>
                                                                        </div>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: token.colorText }}>
                                                                            <span>Prowizja kanału:</span>
                                                                            <span style={{ color: isZeroProfit ? token.colorTextDescription : token.colorError }}>
                                                                                {isZeroProfit ? '0.00 PLN' : (channelCommission > 0 ? `-${channelCommission.toFixed(2)} PLN` : '0.00 PLN')}
                                                                            </span>
                                                                        </div>
                                                                        {channelOtherFees > 0 && (
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: token.colorText }}>
                                                                                <span>Inne opłaty kanału:</span>
                                                                                <span style={{ color: isZeroProfit ? token.colorTextDescription : token.colorError }}>
                                                                                    {isZeroProfit ? '0.00 PLN' : `-${channelOtherFees.toFixed(2)} PLN`}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}

                                                                <Divider style={{ margin: '4px 0' }} />
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 12, color: token.colorText }}>
                                                                    <span>Suma przypisanych kosztów:</span>
                                                                    <span style={{ color: isZeroProfit ? token.colorTextDescription : token.colorError }}>
                                                                        {isZeroProfit ? '0.00 PLN' : `-${totalCostsSum.toFixed(2)} PLN`}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </Col>
                                                    </Row>

                                                    <div style={{ 
                                                        marginTop: 12, 
                                                        padding: '8px 12px', 
                                                        background: token.colorFillAlter, 
                                                        borderRadius: 6,
                                                        border: `1px solid ${token.colorBorderSecondary}`,
                                                        fontSize: 11,
                                                        color: token.colorTextDescription
                                                    }}>
                                                        <div style={{ fontWeight: '600', color: token.colorTextSecondary, marginBottom: 4 }}>
                                                            ℹ️ Koszty nieujęte w wyniku tego zamówienia:
                                                        </div>
                                                        <ul style={{ margin: 0, paddingLeft: 16 }}>
                                                            <li>Koszty marketingu ogólnego (np. Google Ads, Facebook Ads, Allegro Ads rozliczane na poziomie konta).</li>
                                                            <li>Opłaty stałe platform handlowych (np. abonamenty sklepów, prowizje B2B nieujęte w zamówieniu).</li>
                                                            <li>Koszty operacyjne magazynu (wynajem, pakowanie, folia bąbelkowa, kartony).</li>
                                                            <li>Podatki dochodowe oraz VAT (wyniki są kwotami transakcyjnymi).</li>
                                                        </ul>
                                                        {profitability.status === 'INCOMPLETE_COGS' && (
                                                            <div style={{ color: '#d46b08', fontWeight: 'bold', marginTop: 6 }}>
                                                                ⚠️ Uwaga: Jeden lub więcej produktów nie ma ceny zakupu (COGS). Wynik jest zawyżony!
                                                            </div>
                                                        )}
                                                        {profitability.status === 'ESTIMATED_ALLEGRO' && (
                                                            <div style={{ color: '#d46b08', fontWeight: 'bold', marginTop: 6 }}>
                                                                ⏳ Uwaga: Oczekiwanie na synchronizację prowizji z API Allegro. Koszty mogą ulec zmianie.
                                                            </div>
                                                        )}
                                                        {profitability.status === 'COGS_ONLY' && (
                                                            <div style={{ color: token.colorInfoTextActive, fontWeight: 'bold', marginTop: 6 }}>
                                                                💡 Info: Dla tego kanału nie ma automatycznego pobierania prowizji transakcyjnych ani kosztów kuriera. Jeśli chcesz je uwzględnić, wprowadź je w edycji zamówienia.
                                                            </div>
                                                        )}
                                                    </div>
                                                </Card>
                                            );
                                        })()}

                                        {/* 3. Operacje i Wysyłka */}
                                        <Card size="small" title={<span>📦 Operacje i Wysyłka</span>}>
                                            <Row gutter={[16, 16]}>
                                                <Col xs={24} md={12}>
                                                    {(() => {
                                                        const shipment = getShipmentStatus(selectedOrder);
                                                        const trackingUrl = selectedOrder.trackingNumber ? getTrackingUrl(selectedOrder.trackingNumber, selectedOrder.shippingMethod) : null;
                                                        return (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `1px solid ${token.colorBorderSecondary}`, paddingBottom: 4 }}>
                                                                    <span style={{ color: token.colorTextSecondary, fontSize: 13 }}>Metoda dostawy:</span>
                                                                    <span style={{ fontWeight: '500', color: token.colorText }}>{selectedOrder.shippingMethod || '-'}</span>
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `1px solid ${token.colorBorderSecondary}`, paddingBottom: 4 }}>
                                                                    <span style={{ color: token.colorTextSecondary, fontSize: 13 }}>Magazyn:</span>
                                                                    <span style={{ color: token.colorText }}>{selectedOrder.location?.name || '-'}</span>
                                                                </div>
                                                                
                                                                {selectedOrder.trackingNumber ? (
                                                                    <div style={{ marginTop: 8, padding: '10px 12px', background: token.colorFillAlter, borderRadius: 6, border: `1px dashed ${token.colorBorder}` }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                                                            <span style={{ fontSize: 12, color: token.colorTextDescription }}>Status przesyłki:</span>
                                                                            <Tag color={shipment.color} style={{ margin: 0, fontWeight: 600 }}>{shipment.label}</Tag>
                                                                        </div>
                                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                                                            <span style={{ fontSize: 12, color: token.colorTextDescription }}>Numer przesyłki:</span>
                                                                            <Space size={4}>
                                                                                <span style={{ fontWeight: '600', fontFamily: 'monospace', fontSize: 13 }}>{selectedOrder.trackingNumber}</span>
                                                                                <Tooltip title="Kopiuj numer">
                                                                                    <Button 
                                                                                        type="text" 
                                                                                        size="small" 
                                                                                        icon={<CopyOutlined style={{ fontSize: 12 }} />} 
                                                                                        onClick={() => {
                                                                                            navigator.clipboard.writeText(selectedOrder.trackingNumber);
                                                                                            message.success('Skopiowano numer przesyłki!');
                                                                                        }}
                                                                                        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22 }}
                                                                                    />
                                                                                </Tooltip>
                                                                            </Space>
                                                                        </div>
                                                                        {trackingUrl && (
                                                                            <div style={{ marginTop: 8 }}>
                                                                                <Button 
                                                                                    type="primary" 
                                                                                    icon={<LinkOutlined />} 
                                                                                    href={trackingUrl} 
                                                                                    target="_blank" 
                                                                                    rel="noopener noreferrer" 
                                                                                    block
                                                                                    size="small"
                                                                                >
                                                                                    Śledź przesyłkę 🔗
                                                                                </Button>
                                                                            </div>
                                                                        )}
                                                                        {(selectedOrder.channel?.name?.toLowerCase().includes('allegro') || !!selectedOrder.externalId) && (
                                                                            <div style={{ marginTop: 8, fontSize: 11, color: token.colorTextDescription, display: 'flex', gap: 4, alignItems: 'flex-start', lineHeight: '14px' }}>
                                                                                <span>⚠️</span>
                                                                                <span>Status przesyłki pochodzi z Allegro. Dokładny stan doręczenia należy weryfikować u przewoźnika.</span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ marginTop: 8, padding: '10px 12px', background: token.colorFillAlter, borderRadius: 6, border: `1px dashed ${token.colorBorder}`, textAlign: 'center', color: token.colorTextDescription }}>
                                                                        Brak wygenerowanej przesyłki
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })()}
                                                </Col>
                                                
                                                <Col xs={24} md={12}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                        <div style={{ fontSize: 12, fontWeight: '600', color: token.colorTextSecondary, marginBottom: 2 }}>Oś czasu wysyłki:</div>
                                                        <Timeline
                                                            style={{ marginTop: 8 }}
                                                            items={[
                                                                {
                                                                    color: 'green',
                                                                    children: (
                                                                        <div>
                                                                            <strong style={{ color: token.colorText }}>Złożenie zamówienia</strong>
                                                                            <div style={{ color: token.colorTextDescription, fontSize: 11 }}>
                                                                                {dayjs(selectedOrder.orderDate || selectedOrder.createdAt).format('DD.MM.YYYY HH:mm')}
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                },
                                                                ...(selectedOrder.shippedAt ? [
                                                                    {
                                                                        color: 'blue',
                                                                        children: (
                                                                            <div>
                                                                                <strong style={{ color: token.colorText }}>Wysłano</strong>
                                                                                <div style={{ color: token.colorTextDescription, fontSize: 11 }}>
                                                                                    {dayjs(selectedOrder.shippedAt).format('DD.MM.YYYY HH:mm')}
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    }
                                                                ] : []),
                                                                ...(selectedOrder.deliveredAt ? [
                                                                    {
                                                                        color: 'green',
                                                                        children: (
                                                                            <div>
                                                                                <strong style={{ color: token.colorText }}>Dostarczono</strong>
                                                                                <div style={{ color: token.colorTextDescription, fontSize: 11 }}>
                                                                                    {dayjs(selectedOrder.deliveredAt).format('DD.MM.YYYY HH:mm')}
                                                                                </div>
                                                                            </div>
                                                                        )
                                                                    }
                                                                ] : [])
                                                            ]}
                                                        />
                                                        {/* Full Status History embedded inside the Operations card under the shipping timeline */}
                                                        <Collapse ghost size="small" style={{ marginTop: 8 }} items={[
                                                            {
                                                                key: 'status-history',
                                                                label: <span style={{ fontSize: 11, fontWeight: '600', color: token.colorTextSecondary }}>📋 Pokaż pełną historię statusów</span>,
                                                                children: <StatusHistoryCard orderId={selectedOrder.id} plainLayout />
                                                            }
                                                        ]} />
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card>

                                        {/* 4. Pozycje zamówienia */}
                                        <Card size="small" title="Pozycje zamówienia" styles={{ body: { padding: isMobile ? 0 : 8 } }}>
                                            {isMobile ? (
                                                <div style={{ padding: '0 12px' }}>
                                                    <List
                                                        dataSource={displayLines}
                                                        renderItem={(line: any) => (
                                                            <List.Item style={{ padding: '8px 0' }}>
                                                                <div style={{ width: '100%' }}>
                                                                    <div style={{ fontWeight: 600, fontSize: 13, color: token.colorText }}>{line.name}</div>
                                                                    <div style={{ color: token.colorTextDescription, fontSize: 11 }}>SKU: {line.sku}</div>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, color: token.colorText }}>
                                                                        <span>{line.quantity} × {Number(line.unitPrice).toFixed(2)} PLN</span>
                                                                        <span style={{ fontWeight: 600 }}>{Number(line.totalPrice || line.total).toFixed(2)} PLN</span>
                                                                    </div>
                                                                </div>
                                                            </List.Item>
                                                        )}
                                                    />
                                                    <div style={{ background: token.colorFillAlter, padding: '10px 12px', margin: '0 -12px', borderTop: `1px solid ${token.colorBorderSecondary}` }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: 14, color: token.colorText }}>
                                                            <span>RAZEM:</span>
                                                            <span>{totalAmount.toFixed(2)} PLN</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <Table
                                                    dataSource={displayLines}
                                                    rowKey="id"
                                                    size="small"
                                                    pagination={false}
                                                    columns={[
                                                        { title: 'Produkt', dataIndex: 'name', key: 'name' },
                                                        { title: 'SKU', dataIndex: 'sku', key: 'sku', width: 120 },
                                                        { title: 'Ilość', dataIndex: 'quantity', key: 'quantity', width: 80, align: 'center' },
                                                        {
                                                            title: 'Cena jedn.',
                                                            dataIndex: 'unitPrice',
                                                            key: 'unitPrice',
                                                            width: 120,
                                                            align: 'right',
                                                            render: (v: number) => `${Number(v).toFixed(2)} PLN`,
                                                        },
                                                        {
                                                            title: 'Wartość',
                                                            dataIndex: 'total',
                                                            key: 'total',
                                                            width: 130,
                                                            align: 'right',
                                                            render: (v: number) => `${Number(v).toFixed(2)} PLN`,
                                                        },
                                                    ]}
                                                    summary={() => {
                                                        return (
                                                            <Table.Summary fixed>
                                                                <Table.Summary.Row style={{ background: token.colorFillAlter }}>
                                                                    <Table.Summary.Cell index={0} colSpan={4}><strong style={{ color: token.colorText }}>RAZEM</strong></Table.Summary.Cell>
                                                                    <Table.Summary.Cell index={1} align="right">
                                                                        <strong style={{ color: token.colorText }}>{totalAmount.toFixed(2)} PLN</strong>
                                                                    </Table.Summary.Cell>
                                                                </Table.Summary.Row>
                                                            </Table.Summary>
                                                        );
                                                    }}
                                                />
                                            )}
                                        </Card>

                                        {/* 5. Notatki */}
                                        {(selectedOrder.customerNote || selectedOrder.internalNote) && (
                                            <Card size="small" title="Notatki">
                                                {selectedOrder.customerNote && (
                                                    <div style={{ marginBottom: selectedOrder.internalNote ? 12 : 0 }}>
                                                        <div style={{ color: token.colorTextSecondary, fontSize: 12 }}>Notatka od klienta:</div>
                                                        <div style={{ whiteSpace: 'pre-wrap', background: token.colorFillAlter, padding: 8, borderRadius: 4, border: `1px solid ${token.colorBorderSecondary}`, color: token.colorText }}>
                                                            {selectedOrder.customerNote}
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedOrder.internalNote && (
                                                    <div>
                                                        <div style={{ color: token.colorTextSecondary, fontSize: 12 }}>Notatka wewnętrzna:</div>
                                                        <div style={{ whiteSpace: 'pre-wrap', background: token.colorWarningBg, padding: 8, borderRadius: 4, border: `1px solid ${token.colorWarningBorder}`, color: token.colorText }}>
                                                            {selectedOrder.internalNote}
                                                        </div>
                                                    </div>
                                                )}
                                            </Card>
                                        )}

                                        {/* 6. Dane adresowe i identyfikatory techniczne (collapsible) */}
                                        <Collapse ghost size="small" items={[
                                            {
                                                key: 'technical-details',
                                                label: <span style={{ color: token.colorTextDescription, fontSize: 13, fontWeight: '500' }}>📋 Dane adresowe i identyfikatory techniczne</span>,
                                                children: (
                                                    <Row gutter={[16, 16]}>
                                                        <Col xs={24} sm={12}>
                                                            <div style={{ fontWeight: '600', marginBottom: 8, color: token.colorTextSecondary }}>Dane dostawy:</div>
                                                            <p style={{ margin: '4px 0', color: token.colorText }}><strong>Odbiorca:</strong> {selectedOrder.shippingName || selectedOrder.customerName || '-'}</p>
                                                            <p style={{ margin: '4px 0', color: token.colorText }}><strong>Ulica:</strong> {selectedOrder.shippingStreet || '-'}</p>
                                                            <p style={{ margin: '4px 0', color: token.colorText }}><strong>Adres:</strong> {selectedOrder.shippingPostalCode} {selectedOrder.shippingCity}</p>
                                                            <p style={{ margin: '4px 0', color: token.colorText }}><strong>Telefon:</strong> {selectedOrder.shippingPhone || '-'}</p>
                                                            <p style={{ margin: '4px 0', color: token.colorText }}><strong>Email:</strong> {selectedOrder.customerEmail || '-'}</p>
                                                        </Col>
                                                        <Col xs={24} sm={12}>
                                                            <div style={{ fontWeight: '600', marginBottom: 8, color: token.colorTextSecondary }}>Metadane systemowe:</div>
                                                            <p style={{ margin: '4px 0', color: token.colorText }}><strong>ID w bazie ERP:</strong> <span style={{ fontSize: 11, fontFamily: 'monospace', background: token.colorFillAlter, padding: '2px 4px', borderRadius: 2 }}>{selectedOrder.id}</span></p>
                                                            {selectedOrder.orderNumber && (
                                                                <p style={{ margin: '4px 0', color: token.colorText }}><strong>Zewnętrzny identyfikator (np. Allegro):</strong> <span style={{ fontSize: 11, fontFamily: 'monospace', background: token.colorFillAlter, padding: '2px 4px', borderRadius: 2 }}>{selectedOrder.orderNumber}</span></p>
                                                            )}
                                                            <p style={{ margin: '4px 0', color: token.colorText }}><strong>Magazyn realizujący:</strong> {selectedOrder.location?.name || '-'}</p>
                                                            <p style={{ margin: '4px 0', color: token.colorText }}><strong>Data utworzenia w ERP:</strong> {dayjs(selectedOrder.createdAt).format('DD.MM.YYYY HH:mm')}</p>
                                                        </Col>
                                                    </Row>
                                                )
                                            }
                                        ]} />
                                    </Space>
                                </Col>

                                {/* Right sidebar column */}
                                <Col xs={24} lg={8}>
                                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                        {/* 1. Operacje systemowe */}
                                        <Card size="small" title="Operacje systemowe">
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                                <div>
                                                    <div style={{ fontWeight: '600', marginBottom: 8, fontSize: 13, color: token.colorTextSecondary }}>Zmień status:</div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                        {selectedOrder.status === 'PENDING' && (
                                                            <Button
                                                                type="primary"
                                                                onClick={() => updateStatusMutation.mutate({ id: selectedOrder.id, status: 'CONFIRMED' })}
                                                                loading={updateStatusMutation.isPending}
                                                                block
                                                            >
                                                                Potwierdź
                                                            </Button>
                                                        )}
                                                        {['PENDING', 'CONFIRMED'].includes(selectedOrder.status) && (
                                                            <Button
                                                                onClick={() => updateStatusMutation.mutate({ id: selectedOrder.id, status: 'PROCESSING' })}
                                                                loading={updateStatusMutation.isPending}
                                                                block
                                                            >
                                                                Rozpocznij realizację
                                                            </Button>
                                                        )}
                                                        {selectedOrder.status === 'PROCESSING' && (
                                                            <Button
                                                                onClick={() => updateStatusMutation.mutate({ id: selectedOrder.id, status: 'PACKED' })}
                                                                loading={updateStatusMutation.isPending}
                                                                block
                                                            >
                                                                Gotowe do wysyłki
                                                            </Button>
                                                        )}
                                                        {selectedOrder.status === 'PACKED' && (
                                                            <Button
                                                                onClick={() => updateStatusMutation.mutate({ id: selectedOrder.id, status: 'SHIPPED' })}
                                                                loading={updateStatusMutation.isPending}
                                                                block
                                                            >
                                                                Wyślij
                                                            </Button>
                                                        )}
                                                        {selectedOrder.status === 'SHIPPED' && (
                                                            <Button
                                                                onClick={() => updateStatusMutation.mutate({ id: selectedOrder.id, status: 'DELIVERED' })}
                                                                loading={updateStatusMutation.isPending}
                                                                block
                                                            >
                                                                Dostarczono
                                                            </Button>
                                                        )}
                                                        {selectedOrder.status === 'DELIVERED' && (
                                                            <Button
                                                                type="primary"
                                                                onClick={() => updateStatusMutation.mutate({ id: selectedOrder.id, status: 'COMPLETED' })}
                                                                loading={updateStatusMutation.isPending}
                                                                block
                                                            >
                                                                Zakończ
                                                            </Button>
                                                        )}
                                                        <Button
                                                            onClick={() => setIsStatusModalOpen(true)}
                                                            block
                                                        >
                                                            Wybierz status...
                                                        </Button>
                                                        {!['CANCELLED', 'COMPLETED', 'REFUNDED'].includes(selectedOrder.status) && (
                                                            <Button
                                                                danger
                                                                onClick={() => updateStatusMutation.mutate({ id: selectedOrder.id, status: 'CANCELLED' })}
                                                                loading={updateStatusMutation.isPending}
                                                                block
                                                            >
                                                                Anuluj zamówienie
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                                <Divider style={{ margin: '8px 0' }} />
                                                <div>
                                                    <div style={{ fontWeight: '600', marginBottom: 8, fontSize: 13, color: token.colorTextSecondary }}>Zarządzanie rekordem:</div>
                                                    <Row gutter={8}>
                                                        <Col span={12}>
                                                            <Button
                                                                type="primary"
                                                                icon={<EditOutlined />}
                                                                onClick={handleEditOrder}
                                                                block
                                                            >
                                                                Edytuj
                                                            </Button>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Button
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                onClick={handleDeleteOrder}
                                                                block
                                                            >
                                                                Usuń
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            </div>
                                        </Card>

                                        {/* 2. Order Comments */}
                                        <OrderComments orderId={selectedOrder.id} isMobile={isMobile} />

                                        {/* 3. Contact History from CRM (collapsible) */}
                                        <Collapse ghost size="small" items={[
                                            {
                                                key: 'crm-history',
                                                label: (
                                                    <Space style={{ color: token.colorTextDescription, fontSize: 13, fontWeight: '500' }}>
                                                        <PhoneOutlined />
                                                        <span>Historia kontaktów (CRM)</span>
                                                    </Space>
                                                ),
                                                extra: (
                                                    <Button
                                                        size="small"
                                                        icon={<HistoryOutlined />}
                                                        onClick={async (e) => {
                                                            e.stopPropagation();
                                                            try {
                                                                const email = selectedOrder.customerEmail || selectedOrder.customer?.email;
                                                                if (email) {
                                                                    const res = await crmApi.getHistoryByEmail(email);
                                                                    setOrderContactHistory(res.data);
                                                                } else {
                                                                    const res = await crmApi.getHistoryByOrder(selectedOrder.id);
                                                                    setOrderContactHistory(res.data);
                                                                }
                                                            } catch {
                                                                message.error('Błąd pobierania historii kontaktów');
                                                            }
                                                        }}
                                                    >
                                                        Odśwież
                                                    </Button>
                                                ),
                                                children: (
                                                    orderContactHistory.length > 0 ? (
                                                        isMobile ? (
                                                            <List
                                                                dataSource={orderContactHistory}
                                                                renderItem={(record: any) => {
                                                                    const colors: Record<string, string> = {
                                                                        PENDING: 'default',
                                                                        CONTACTED: 'blue',
                                                                        NO_ANSWER: 'orange',
                                                                        REVIEW_REQUESTED: 'green',
                                                                        DECLINED: 'red',
                                                                        CALLBACK: 'purple',
                                                                    };
                                                                    const labels: Record<string, string> = {
                                                                        PENDING: 'Do kontaktu',
                                                                        CONTACTED: 'Skontaktowany',
                                                                        NO_ANSWER: 'Brak odpowiedzi',
                                                                        REVIEW_REQUESTED: 'Prośba o opinię',
                                                                        DECLINED: 'Odmówił',
                                                                        CALLBACK: 'Oddzwonić',
                                                                    };
                                                                    const reviewLabels: Record<string, string> = {
                                                                        NONE: '-',
                                                                        PENDING: 'Oczekuje',
                                                                        RECEIVED: 'Otrzymano',
                                                                        NOT_RECEIVED: 'Nie zostawił',
                                                                    };
                                                                    return (
                                                                        <List.Item style={{ padding: '8px 0', borderBottom: `1px solid ${token.colorBorderSecondary}` }}>
                                                                            <div style={{ width: '100%', color: token.colorText }}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                                                                    <strong>{dayjs(record.contactedAt || record.createdAt).format('DD.MM.YYYY HH:mm')}</strong>
                                                                                    <Tag color={colors[record.contactStatus]}>{labels[record.contactStatus] || record.contactStatus}</Tag>
                                                                                </div>
                                                                                <div style={{ fontSize: 12 }}>
                                                                                    <strong>Opinia:</strong> {reviewLabels[record.reviewStatus] || record.reviewStatus}
                                                                                </div>
                                                                                {record.notes && (
                                                                                    <div style={{ marginTop: 4, fontStyle: 'italic', fontSize: 11, color: token.colorTextSecondary }}>
                                                                                        {record.notes}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </List.Item>
                                                                    );
                                                                }}
                                                            />
                                                        ) : (
                                                            <Table
                                                                dataSource={orderContactHistory}
                                                                rowKey="id"
                                                                size="small"
                                                                pagination={false}
                                                                columns={[
                                                                    {
                                                                        title: 'Data',
                                                                        dataIndex: 'contactedAt',
                                                                        render: (date: string, record: any) =>
                                                                            date ? dayjs(date).format('DD.MM.YYYY HH:mm') :
                                                                                dayjs(record.createdAt).format('DD.MM.YYYY'),
                                                                        width: 120,
                                                                    },
                                                                    {
                                                                        title: 'Status',
                                                                        dataIndex: 'contactStatus',
                                                                        render: (status: string) => {
                                                                            const colors: Record<string, string> = {
                                                                                PENDING: 'default',
                                                                                CONTACTED: 'blue',
                                                                                NO_ANSWER: 'orange',
                                                                                REVIEW_REQUESTED: 'green',
                                                                                DECLINED: 'red',
                                                                                CALLBACK: 'purple',
                                                                            };
                                                                            const labels: Record<string, string> = {
                                                                                PENDING: 'Do kontaktu',
                                                                                CONTACTED: 'Skontaktowany',
                                                                                NO_ANSWER: 'Brak odpowiedzi',
                                                                                REVIEW_REQUESTED: 'Prośba o opinię',
                                                                                DECLINED: 'Odmówił',
                                                                                CALLBACK: 'Oddzwonić',
                                                                                };
                                                                            return <Tag color={colors[status]}>{labels[status] || status}</Tag>;
                                                                        },
                                                                        width: 110,
                                                                    },
                                                                    {
                                                                        title: 'Notatki',
                                                                        dataIndex: 'notes',
                                                                        render: (notes: string) => notes ? (
                                                                            <span style={{ fontSize: 11, color: token.colorText }}>{notes.substring(0, 50)}...</span>
                                                                        ) : '-',
                                                                    },
                                                                ]}
                                                            />
                                                        )
                                                    ) : (
                                                        <div style={{ color: token.colorTextDescription, textAlign: 'center', padding: 8, fontSize: 12 }}>
                                                            Kliknij "Odśwież" aby załadować historię kontaktów
                                                        </div>
                                                    )
                                                )
                                            }
                                        ]} />
                                    </Space>
                                </Col>
                            </Row>
                        </Space>
                    );
                })()}
            </Modal>

            {/* Status Change Modal */}
            <Modal
                title="Zmień status zamówienia"
                open={isStatusModalOpen}
                onCancel={() => {
                    setIsStatusModalOpen(false);
                    statusForm.resetFields();
                }}
                onOk={() => statusForm.submit()}
                confirmLoading={updateStatusMutation.isPending}
                width={isMobile ? '100%' : 520}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
            >
                <Form form={statusForm} layout="vertical" onFinish={handleStatusChange}>
                    <Form.Item name="status" label="Nowy status" rules={[{ required: true, message: 'Wybierz status' }]}>
                        <Select
                            options={[
                                { value: 'PENDING', label: 'Oczekujące' },
                                { value: 'CONFIRMED', label: 'Potwierdzone' },
                                { value: 'PROCESSING', label: 'W realizacji' },
                                { value: 'PACKED', label: 'Gotowe do wysyłki' },
                                { value: 'SHIPPED', label: 'Wysłane' },
                                { value: 'DELIVERED', label: 'Dostarczone' },
                                { value: 'COMPLETED', label: 'Zakończone' },
                                { value: 'CANCELLED', label: 'Anulowane' },
                                { value: 'REFUNDED', label: 'Zwrócone' },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.status !== cur.status}>
                        {({ getFieldValue }) => {
                            const status = getFieldValue('status');
                            if (status === 'SHIPPED' || status === 'DELIVERED') {
                                return (
                                    <Form.Item
                                        name="statusDate"
                                        label={status === 'SHIPPED' ? 'Data wysyłki' : 'Data dostawy'}
                                        initialValue={dayjs()}
                                        tooltip="Domyślnie: teraz. Możesz zmienić na rzeczywistą datę."
                                    >
                                        <DatePicker
                                            showTime
                                            format="DD.MM.YYYY HH:mm"
                                            style={{ width: '100%' }}
                                            placeholder="Wybierz datę i godzinę"
                                        />
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                    <Form.Item name="reason" label="Powód zmiany (opcjonalny)">
                        <Input.TextArea rows={2} placeholder="Np. klient poprosił o szybszą wysyłkę..." />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Edit Order Modal */}
            <Modal
                title={`Edytuj zamówienie ${selectedOrder ? getShortOrderNumber(selectedOrder) : ''}`}
                open={isEditModalOpen}
                onCancel={() => {
                    setIsEditModalOpen(false);
                    editForm.resetFields();
                    setSelectedEditContractor(null);
                }}
                onOk={() => editForm.submit()}
                confirmLoading={updateOrderMutation.isPending}
                width={isMobile ? '100%' : 700}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
            >
                <Form form={editForm} layout="vertical" onFinish={handleEditSubmit}>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="orderDate"
                                label="Data zamówienia"
                                tooltip="Zmień datę zamówienia dla celów statystyk"
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD.MM.YYYY"
                                    showTime={false}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="contractorId" label="Kontrahent B2B" help="Wybierz kontrahenta, aby automatycznie uzupełnić dane.">
                                <ContractorSelect
                                    onChange={handleEditContractorSelect}
                                    value={selectedEditContractor?.id}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name={['customFields', 'salesType']} label="Typ sprzedaży">
                                <Select
                                    options={[
                                        { label: 'Detal', value: 'detal' },
                                        { label: 'B2B', value: 'b2b' },
                                        { label: 'Komis', value: 'komis' },
                                    ]}
                                    placeholder="Wybierz typ sprzedaży"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    {selectedEditContractor && (
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={24}>
                                <ContractorMiniInfo contractor={selectedEditContractor} />
                            </Col>
                        </Row>
                    )}

                    <Divider>Dane klienta</Divider>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="shippingName" label="Imię i nazwisko / firma">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="shippingPhone" label="Telefon">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>Adres dostawy</Divider>
                    <Form.Item name="shippingStreet" label="Ulica i numer">
                        <Input />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col xs={24} sm={8}>
                            <Form.Item name="shippingPostalCode" label="Kod pocztowy">
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={16}>
                            <Form.Item name="shippingCity" label="Miasto">
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>Wysyłka i płatność</Divider>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="shippingMethod" label="Metoda wysyłki">
                                <Input placeholder="np. Kurier DPD" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="trackingNumber" label="Numer śledzenia">
                                <Input placeholder="np. 123456789" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="paymentMethod" label="Metoda płatności">
                                <Input placeholder="np. Przelew, PayPal" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="paymentStatus" label="Status płatności">
                                <Select
                                    options={[
                                        { value: 'PENDING', label: 'Oczekuje' },
                                        { value: 'PAID', label: 'Zapłacone' },
                                        { value: 'PARTIALLY_PAID', label: 'Częściowe' },
                                        { value: 'REFUNDED', label: 'Zwrot' },
                                        { value: 'FAILED', label: 'Błąd' },
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev?.paymentStatus !== cur?.paymentStatus}>
                        {({ getFieldValue }) => getFieldValue('paymentStatus') === 'PARTIALLY_PAID' && (
                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item name="paidAmount" label="Wpłacona kwota">
                                        <InputNumber style={{ width: '100%' }} min={0} precision={2} addonAfter="PLN" />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                    </Form.Item>

                    <Divider>Zwrot (opcjonalne)</Divider>
                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item name="refundAmount" label="Kwota zwrotu">
                                <InputNumber style={{ width: '100%' }} min={0} precision={2} addonAfter="PLN" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item name="goodsReturned" label="Towar zwrócony?" valuePropName="checked">
                                <Switch checkedChildren="Tak" unCheckedChildren="Nie" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>Notatki</Divider>
                    <Form.Item name="customerNote" label="Notatki dla klienta">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item name="internalNote" label="Notatki wewnętrzne">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Save Filter Modal */}
            <Modal
                title="Zapisz filtr"
                open={isSaveFilterModalOpen}
                onCancel={() => setIsSaveFilterModalOpen(false)}
                onOk={() => saveFilterForm.submit()}
                okText="Zapisz"
                cancelText="Anuluj"
                confirmLoading={createFilterMutation.isPending}
                width={isMobile ? '100%' : 520}
                style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw' } : {}}
            >
                <Form form={saveFilterForm} onFinish={handleSaveFilter} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Nazwa filtra"
                        rules={[{ required: true, message: 'Podaj nazwę filtra' }]}
                    >
                        <Input placeholder="np. Zamówienia oczekujące" />
                    </Form.Item>
                    <p style={{ color: '#666', fontSize: 12 }}>
                        Zapisane zostaną aktualne ustawienia filtrów: {status ? `Status: ${status}` : 'Brak filtrów'}
                    </p>
                </Form>
            </Modal >

            {selectedRowKeys.length > 0 && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        background: token.colorBgElevated,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: 12,
                        padding: '12px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        flexWrap: 'wrap',
                        borderLeft: `4px solid ${token.colorPrimary}`
                    }}
                >
                    <span style={{ fontWeight: 600, color: token.colorText }}>
                        Zaznaczono: <Tag color="blue" style={{ margin: 0, fontSize: 12 }}>{selectedRowKeys.length}</Tag>
                    </span>
                    <Divider type="vertical" style={{ height: 20, margin: 0 }} />
                    <Space size="middle">
                        <Button
                            icon={<DownloadOutlined />}
                            size="small"
                            onClick={handleBulkExport}
                        >
                            Eksportuj CSV
                        </Button>
                        <Dropdown
                            menu={{
                                items: [
                                    { key: 'CONFIRMED', label: 'Potwierdzone' },
                                    { key: 'PROCESSING', label: 'W realizacji' },
                                    { key: 'PACKED', label: 'Spakowane' },
                                    { key: 'SHIPPED', label: 'Wysłane' },
                                    { key: 'COMPLETED', label: 'Zakończone' }
                                ],
                                onClick: ({ key }) => handleBulkStatusUpdate(key)
                            }}
                            trigger={['click']}
                        >
                            <Button size="small" icon={<EditOutlined />} loading={bulkUpdateStatusMutation.isPending}>
                                Zmień status <DownOutlined />
                            </Button>
                        </Dropdown>
                        <Button
                            size="small"
                            type="text"
                            danger
                            onClick={() => setSelectedRowKeys([])}
                            style={{ padding: '0 4px' }}
                        >
                            Anuluj
                        </Button>
                    </Space>
                </div>
            )}
        </Space >
    );
}
